import userMessageReceivedSoundSrc from "../assets/user-message-received.mp3";
import userMessageSentSoundSrc from "../assets/user-message-sent.mp3";

import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { RuntimeConfigContext } from "@/runtime/RuntimeConfigContext";
import { acquireSilentWidgetAuth } from "@/runtime/auth/silentWidgetAuth";
import {
  type AppendMessage,
  AssistantRuntimeProvider,
  AuiIf,
  type AssistantRuntime,
  type ChatModelRunOptions,
  type ChatModelRunResult,
  type ThreadMessage,
  type ThreadMessageLike,
  useExternalStoreRuntime,
} from "@assistant-ui/react";
import {
  AssistantLauncherUnreadBadge,
  AssistantModal,
  AssistantModalButton,
  LauncherNudge,
  resetLauncherNudgeResolution,
  WindowLevelError,
  WindowLevelLoader,
  type WindowContentState,
} from "@/components/assistant-ui/assistant-modal";
import { useHostBottomObstructionHeights } from "@/components/assistant-ui/host-bottom-action-bar";
import { hasVisibleMessageContent } from "@/components/assistant-ui/message-content";
import {
  getHostDockingController,
  type HostDockingController,
  type HostDockingLease,
} from "@/lib/hostDocking";
import {
  ChatHomeViewLegalNotice,
  ComposerSubmissionContext,
  scheduleInitialChatHeadingFocus,
  Thread,
  ThreadHeader,
} from "@/components/assistant-ui/thread";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  EMPTY_LEGAL_NOTICE_REMINDER_STATE,
  LEGAL_NOTICE_REMINDER_INTERVAL_MS,
  LEGAL_NOTICE_REMINDER_STORAGE_KEY,
  ensureLegalNoticeThreadStarted,
  readLegalNoticeReminderState,
  removeLegalNoticeReminder,
  writeLegalNoticeReminderState,
  type LegalNoticeReminderState,
} from "@/runtime/legalNoticeReminder";
import { createSocketChatRuntimeController } from "@/runtime/socket/socketChatModelAdapter";
import { sanitizeSupervisorResponseChunk } from "@/runtime/socket/socketConnection";
import { sendWidgetTelemetry } from "@/runtime/socket/telemetry";
import { resumeTurnsToThreadMessages } from "@/runtime/socket/socketHistoryAdapter";
import { createClientMessageId, type SocketInitialSessionIdentity } from "@/runtime/socket/sessionIdentity";
import { socketStatusActions, useSocketStatusStore } from "@/runtime/socket/socketStatusStore";
import {
  clearSafetyExitSessionOverride,
  createFreshChatSessionIdentity,
  recordSafetyExitSessionReset,
  resolveSafetyExitSessionIdentityOverride,
  withSafetyExitMetadata,
} from "@/runtime/safetyExit";
import type {
  FollowUpSuggestion,
  GuestSessionBootstrapOptions,
  LiveAssistanceSessionPayload,
  QualityProfile,
  SessionResumePayload,
  SessionTurnPersistedPayload,
  SupervisorHandoffPayload,
  SupervisorLiveEndedPayload,
  SupervisorLiveErrorPayload,
  SupervisorLiveMessagePayload,
  SupervisorResponseChunkPayload,
  SupervisorResponseErrorPayload,
  SupervisorResponseFinalPayload,
  SupervisorRoutingStartedPayload,
  SupervisorStageUpdatedPayload,
  WidgetFeedbackSubmitPayload,
} from "@/runtime/socket/types";
import { track, trackUOPX, getTraits, disable, init } from "@/analytics";
import type { SegmentTraits } from "@/analytics";
import { UOPX_EVENT, UOPX_LOCATION, UOPX_TEXT } from "@/analytics/events";

type ActiveRunState = {
  assistantCreatedAt: Date;
  assistantMessageId: string;
  controller: AbortController;
};

type PassiveRunState = {
  assistantCreatedAt: Date;
  assistantMessageId: string;
  fullText: string;
};

type DateMessageTestCase = "today" | "yesterday" | "current-year" | "prior-year";
type PhoebeDevtools = {
  seedDateMessages: () => void;
  seedTodayMessage: () => void;
  seedYesterdayMessage: () => void;
  seedCurrentYearDateMessage: () => void;
  seedPriorYearDateMessage: () => void;
  clearDateMessages: () => void;
  resetLauncherNudgeState: () => void;
  resetLegalNoticeReminderState: () => void;
  restoreLegalNoticeReminderInterval: () => void;
  setLegalNoticeReminderIntervalMs: (intervalMs: number) => void;
  triggerWindowError: () => void;
  triggerWindowLoader: () => void;
  clearWindowState: () => void;
};

declare global {
  interface Window {
    __PHOEBE_DEVTOOLS__?: Partial<PhoebeDevtools>;
  }
}

function syncLegalNoticeReminderState(state: LegalNoticeReminderState): void {
  writeLegalNoticeReminderState(state);
  window.dispatchEvent(new StorageEvent("storage", {
    key: LEGAL_NOTICE_REMINDER_STORAGE_KEY,
    newValue: JSON.stringify(state),
  }));
}

export type ChatState = "idle" | "chat";
export type ChatShellSyncStatus = "closed" | "open" | "minimized" | "expanded" | "collapsed";
export type ChatShellSyncState = {
  version: 1;
  eventId?: string;
  sourceId: string;
  status: ChatShellSyncStatus;
  layout: ChatModalLayout;
  updatedAt: number;
};
export type ChatConversationSyncAction = "new_chat";
export type ChatConversationSyncState = {
  version: 1;
  eventId?: string;
  sourceId: string;
  action: ChatConversationSyncAction;
  updatedAt: number;
};
export type ChatMessageSyncAction = "message_sent";
export type ChatMessageSyncState = {
  version: 1;
  eventId?: string;
  sourceId: string;
  action: ChatMessageSyncAction;
  guestId?: string;
  threadId?: string;
  memoryId?: string;
  clientMessageId: string;
  text: string;
  createdAt: number;
  updatedAt: number;
};
export type ChatPresenceSyncAction = "tab_focused" | "tab_blurred" | "user_online" | "user_offline";
export type ChatPresenceSyncState = {
  version: 1;
  eventId?: string;
  sourceId: string;
  action: ChatPresenceSyncAction;
  active: boolean;
  updatedAt: number;
};
export type ChatUnreadSyncState = {
  version: 1;
  eventId?: string;
  sourceId: string;
  guestId?: string;
  threadId?: string;
  memoryId: string;
  unreadCount: number;
  updatedAt: number;
};
const USER_MESSAGE_RECEIVED_SOUND_SRC = userMessageReceivedSoundSrc as unknown as string;
const USER_MESSAGE_SENT_SOUND_SRC = userMessageSentSoundSrc as unknown as string;
const SIDE_PANEL_STATE_EVENT = "phoebe:side-panel-state";
const CHAT_SHELL_SYNC_CHANNEL = "phoebe_chat_shell_sync";
const CHAT_CONVERSATION_SYNC_CHANNEL = "phoebe_chat_conversation_sync";
const CHAT_MESSAGE_SYNC_CHANNEL = "phoebe_chat_message_sync";
const CHAT_PRESENCE_SYNC_CHANNEL = "phoebe_chat_presence_sync";
const CHAT_UNREAD_SYNC_CHANNEL = "phoebe_chat_unread_sync";
export const CHAT_SHELL_SYNC_STORAGE_KEY = "phoebe_chat_shell_state";
export const CHAT_CONVERSATION_SYNC_STORAGE_KEY = "phoebe_chat_conversation_state";
export const CHAT_MESSAGE_SYNC_STORAGE_KEY = "phoebe_chat_message_state";
export const CHAT_PRESENCE_SYNC_STORAGE_KEY = "phoebe_chat_presence_state";
export const CHAT_UNREAD_SYNC_STORAGE_KEY = "phoebe_chat_unread_state";
export const CHAT_SESSION_IDENTITY_STORAGE_KEY = "phoebe_chat_session_identity";
const RECEIVED_SOUND_CLAIMS_STORAGE_KEY = "phoebe_received_sound_claims";
const RECEIVED_SOUND_CLAIM_TTL_MS = 10 * 60 * 1000;
const RECEIVED_SOUND_CLAIM_SETTLE_MS = 250;
const RECEIVED_SOUND_CLAIM_MAX_ENTRIES = 50;
const RECEIVED_SOUND_TAB_ID = createSyncSourceId();
const CHAT_SESSION_IDENTITY_VALUE_MAX_LENGTH = 256;
const STREAMING_INDICATOR_MIN_VISIBLE_MS = 800;
const CHAT_SHELL_SYNC_MAX_AGE_MS = 30_000;
const SIDE_PANEL_WIDTH_PX = 446;
const SIDE_PANEL_MOBILE_BREAKPOINT_PX = 576;
const SIDE_PANEL_MOBILE_HEADER_GAP_PX = 4;
const SIDE_PANEL_FOOTER_CLASS =
  "aui-side-panel-footer flex w-full flex-col items-center gap-[8px] self-stretch border-t border-[rgba(0,24,35,0.12)] bg-white p-[16px]";

export type ChatSessionIdentitySyncState = {
  version: 1;
  guestId?: string;
  threadId?: string;
  memoryId?: string;
  updatedAt: number;
};

function createSyncSourceId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `phoebe-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createSyncEventId(prefix: string): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function mobileSidePanelTop(panel: HTMLElement): number {
  if (typeof window === "undefined") return SIDE_PANEL_MOBILE_HEADER_GAP_PX;

  const headerBottom = Array.from(document.querySelectorAll<HTMLElement>("header, [role='banner'], nav"))
    .filter((element) => !panel.contains(element))
    .map((element) => element.getBoundingClientRect())
    .filter((rect) => rect.bottom > 0 && rect.top < window.innerHeight * 0.45)
    .reduce((bottom, rect) => Math.max(bottom, rect.bottom), 0);

  return Math.max(SIDE_PANEL_MOBILE_HEADER_GAP_PX, Math.round(headerBottom) + SIDE_PANEL_MOBILE_HEADER_GAP_PX);
}

function sidePanelViewportIsMobile(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(`(max-width: ${SIDE_PANEL_MOBILE_BREAKPOINT_PX}px)`).matches
    : false;
}

function publishSidePanelReservationState(
  root: HTMLElement,
  geometryActive: boolean,
  isMobile: boolean,
  isMinimized: boolean,
): void {
  root.toggleAttribute("data-phoebe-side-panel-open", geometryActive);
  root.style.setProperty("--phoebe-side-panel-inline-size", geometryActive ? `${SIDE_PANEL_WIDTH_PX}px` : "0px");
  dispatchSidePanelState(geometryActive, isMobile, isMinimized);
}

function dispatchSidePanelState(geometryActive: boolean, isMobile: boolean, isMinimized: boolean): void {
  window.dispatchEvent(new CustomEvent(SIDE_PANEL_STATE_EVENT, {
    detail: {
      layout: "side-panel",
      open: geometryActive,
      minimized: isMinimized,
      mobile: isMobile,
      contentInsetInlineEnd: geometryActive ? SIDE_PANEL_WIDTH_PX : 0,
    },
  }));
}

function clearSidePanelReservationState(root: HTMLElement): void {
  root.removeAttribute("data-phoebe-side-panel-open");
  root.style.removeProperty("--phoebe-side-panel-inline-size");
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function hashSyncValue(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function receivedMessageSoundKey({
  agentId,
  traceId,
  threadId,
  response,
}: {
  agentId?: string | null;
  traceId?: string | null;
  threadId?: string | null;
  response?: string | null;
}): string {
  const normalizedTraceId = traceId?.trim();
  const normalizedThreadId = threadId?.trim() || "unknown-thread";
  if (normalizedTraceId) {
    return `trace:${hashSyncValue(`${normalizedThreadId}:${normalizedTraceId}`)}`;
  }
  const normalizedAgentId = agentId?.trim() || "unknown-agent";
  const normalizedResponse = response?.trim();
  return `response:${hashSyncValue(`${normalizedThreadId}:${normalizedAgentId}:${normalizedResponse || "empty-response"}`)}`;
}

type ReceivedSoundClaim = {
  claimedAt: number;
  ownerId: string;
};

type ReceivedSoundClaimState = {
  version: 1;
  claims: Record<string, ReceivedSoundClaim>;
};

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function readReceivedSoundClaimState(storage: Storage): ReceivedSoundClaimState {
  const rawValue = storage.getItem(RECEIVED_SOUND_CLAIMS_STORAGE_KEY);
  if (!rawValue) return { version: 1, claims: {} };
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawValue);
  } catch {
    return { version: 1, claims: {} };
  }
  if (!isPlainRecord(parsed) || !isPlainRecord(parsed.claims)) {
    return { version: 1, claims: {} };
  }
  const claims = Object.fromEntries(
    Object.entries(parsed.claims).filter(
      ([, claim]) =>
        isPlainRecord(claim) &&
        typeof claim.claimedAt === "number" &&
        typeof claim.ownerId === "string",
    ),
  ) as Record<string, ReceivedSoundClaim>;
  return {
    version: 1,
    claims,
  };
}

function pruneReceivedSoundClaims(
  claims: Record<string, ReceivedSoundClaim>,
  now: number,
): Record<string, ReceivedSoundClaim> {
  return Object.fromEntries(
    Object.entries(claims)
      .filter(
        ([, claim]) =>
          typeof claim.claimedAt === "number" && now - claim.claimedAt < RECEIVED_SOUND_CLAIM_TTL_MS,
      )
      .sort(([, left], [, right]) => right.claimedAt - left.claimedAt)
      .slice(0, RECEIVED_SOUND_CLAIM_MAX_ENTRIES),
  );
}

async function claimReceivedNotificationSound(messageId: string): Promise<boolean> {
  try {
    const storage = getChatShellSyncStorage();
    if (!storage) return true;
    const claimId = hashSyncValue(messageId);
    const now = Date.now();
    const currentClaims = pruneReceivedSoundClaims(readReceivedSoundClaimState(storage).claims, now);
    const currentClaim = currentClaims[claimId];
    if (
      currentClaim &&
      typeof currentClaim.claimedAt === "number" &&
      now - currentClaim.claimedAt < RECEIVED_SOUND_CLAIM_TTL_MS
    ) {
      return false;
    }
    storage.setItem(
      RECEIVED_SOUND_CLAIMS_STORAGE_KEY,
      JSON.stringify({
        version: 1,
        claims: pruneReceivedSoundClaims(
          {
            ...currentClaims,
            [claimId]: { claimedAt: now, ownerId: RECEIVED_SOUND_TAB_ID },
          },
          now,
        ),
      }),
    );
    await delay(RECEIVED_SOUND_CLAIM_SETTLE_MS);
    const settledClaim = readReceivedSoundClaimState(storage).claims[claimId];
    return settledClaim?.ownerId === RECEIVED_SOUND_TAB_ID;
  } catch {
    return true;
  }
}

function createConversationSyncState(action: ChatConversationSyncAction, sourceId: string): ChatConversationSyncState {
  return {
    version: 1,
    eventId: createSyncEventId("conversation"),
    sourceId,
    action,
    updatedAt: Date.now(),
  };
}

function chatShellStatusForState(chatState: ChatState, isMinimized: boolean): ChatShellSyncStatus {
  if (chatState !== "chat") return "closed";
  return isMinimized ? "minimized" : "open";
}

export function parseChatShellSyncState(rawValue: string | null): ChatShellSyncState | null {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue) as Partial<ChatShellSyncState>;
    if (parsed.version !== 1) return null;
    if (parsed.eventId !== undefined && typeof parsed.eventId !== "string") return null;
    if (typeof parsed.sourceId !== "string" || !parsed.sourceId) return null;
    if (
      parsed.status !== "closed" &&
      parsed.status !== "open" &&
      parsed.status !== "minimized" &&
      parsed.status !== "expanded" &&
      parsed.status !== "collapsed"
    ) return null;
    if (parsed.layout !== "launcher" && parsed.layout !== "side-panel") return null;
    if (typeof parsed.updatedAt !== "number" || !Number.isFinite(parsed.updatedAt)) return null;
    return parsed as ChatShellSyncState;
  } catch {
    return null;
  }
}

export function parseChatConversationSyncState(rawValue: string | null): ChatConversationSyncState | null {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue) as Partial<ChatConversationSyncState>;
    if (parsed.version !== 1) return null;
    if (parsed.eventId !== undefined && typeof parsed.eventId !== "string") return null;
    if (typeof parsed.sourceId !== "string" || !parsed.sourceId) return null;
    if (parsed.action !== "new_chat") return null;
    if (typeof parsed.updatedAt !== "number" || !Number.isFinite(parsed.updatedAt)) return null;
    return parsed as ChatConversationSyncState;
  } catch {
    return null;
  }
}

export function parseChatMessageSyncState(rawValue: string | null): ChatMessageSyncState | null {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue) as Partial<ChatMessageSyncState>;
    if (parsed.version !== 1) return null;
    if (parsed.eventId !== undefined && typeof parsed.eventId !== "string") return null;
    if (typeof parsed.sourceId !== "string" || !parsed.sourceId) return null;
    if (parsed.action !== "message_sent") return null;
    const guestId = cleanSessionIdentityValue(parsed.guestId);
    const threadId = cleanSessionIdentityValue(parsed.threadId);
    const memoryId = cleanSessionIdentityValue(parsed.memoryId);
    if (typeof parsed.clientMessageId !== "string" || !parsed.clientMessageId) return null;
    if (typeof parsed.text !== "string" || !parsed.text.trim()) return null;
    if (typeof parsed.createdAt !== "number" || !Number.isFinite(parsed.createdAt)) return null;
    if (typeof parsed.updatedAt !== "number" || !Number.isFinite(parsed.updatedAt)) return null;
    return {
      version: 1,
      ...(parsed.eventId ? { eventId: parsed.eventId } : {}),
      sourceId: parsed.sourceId,
      action: parsed.action,
      ...(guestId ? { guestId } : {}),
      ...(threadId ? { threadId } : {}),
      ...(memoryId ? { memoryId } : {}),
      clientMessageId: parsed.clientMessageId,
      text: parsed.text,
      createdAt: parsed.createdAt,
      updatedAt: parsed.updatedAt,
    } as ChatMessageSyncState;
  } catch {
    return null;
  }
}

export function parseChatPresenceSyncState(rawValue: string | null): ChatPresenceSyncState | null {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue) as Partial<ChatPresenceSyncState>;
    if (parsed.version !== 1) return null;
    if (parsed.eventId !== undefined && typeof parsed.eventId !== "string") return null;
    if (typeof parsed.sourceId !== "string" || !parsed.sourceId) return null;
    if (
      parsed.action !== "tab_focused" &&
      parsed.action !== "tab_blurred" &&
      parsed.action !== "user_online" &&
      parsed.action !== "user_offline"
    ) return null;
    if (typeof parsed.active !== "boolean") return null;
    if (typeof parsed.updatedAt !== "number" || !Number.isFinite(parsed.updatedAt)) return null;
    return parsed as ChatPresenceSyncState;
  } catch {
    return null;
  }
}

export function parseChatUnreadSyncState(rawValue: string | null): ChatUnreadSyncState | null {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue) as Partial<ChatUnreadSyncState>;
    if (parsed.version !== 1) return null;
    if (parsed.eventId !== undefined && typeof parsed.eventId !== "string") return null;
    if (typeof parsed.sourceId !== "string" || !parsed.sourceId) return null;
    const guestId = cleanSessionIdentityValue(parsed.guestId);
    const threadId = cleanSessionIdentityValue(parsed.threadId);
    const memoryId = cleanSessionIdentityValue(parsed.memoryId);
    if (!memoryId) return null;
    if (
      typeof parsed.unreadCount !== "number" ||
      !Number.isInteger(parsed.unreadCount) ||
      parsed.unreadCount < 0 ||
      parsed.unreadCount > 99
    ) return null;
    if (typeof parsed.updatedAt !== "number" || !Number.isFinite(parsed.updatedAt)) return null;
    return {
      version: 1,
      ...(parsed.eventId ? { eventId: parsed.eventId } : {}),
      sourceId: parsed.sourceId,
      ...(guestId ? { guestId } : {}),
      ...(threadId ? { threadId } : {}),
      memoryId,
      unreadCount: parsed.unreadCount,
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}

export function parseChatSessionIdentitySyncState(rawValue: string | null): ChatSessionIdentitySyncState | null {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue) as Partial<ChatSessionIdentitySyncState>;
    if (parsed.version !== 1) return null;
    const guestId = cleanSessionIdentityValue(parsed.guestId);
    const threadId = cleanSessionIdentityValue(parsed.threadId);
    const memoryId = cleanSessionIdentityValue(parsed.memoryId);
    if (!guestId && !threadId && !memoryId) return null;
    if (typeof parsed.updatedAt !== "number" || !Number.isFinite(parsed.updatedAt)) return null;
    return {
      version: 1,
      ...(guestId ? { guestId } : {}),
      ...(threadId ? { threadId } : {}),
      ...(memoryId ? { memoryId } : {}),
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}

export function widgetStateFromChatShellSyncState(
  shellState: Pick<ChatShellSyncState, "status">,
): { chatState: ChatState; isMinimized: boolean } {
  if (shellState.status === "closed") return { chatState: "idle", isMinimized: false };
  return { chatState: "chat", isMinimized: shellState.status === "minimized" };
}

export function isFreshChatShellSyncState(state: ChatShellSyncState): boolean {
  const ageMs = Date.now() - state.updatedAt;
  return ageMs >= 0 && ageMs <= CHAT_SHELL_SYNC_MAX_AGE_MS;
}

export function syncMatchesConversationIdentity(
  syncedState: Pick<ChatMessageSyncState, "guestId" | "threadId" | "memoryId">,
  currentIdentity: Pick<ChatMessageSyncState, "guestId" | "threadId" | "memoryId">,
): boolean {
  const comparableFields = (["guestId", "threadId", "memoryId"] as const).filter(
    (field) => syncedState[field] && currentIdentity[field],
  );
  if (comparableFields.length === 0) return false;
  return comparableFields.every((field) => syncedState[field] === currentIdentity[field]);
}

export function unreadSyncMatchesConversationIdentity(
  syncedState: Pick<ChatUnreadSyncState, "guestId" | "threadId" | "memoryId">,
  currentIdentity: Pick<ChatUnreadSyncState, "guestId" | "threadId" | "memoryId">,
): boolean {
  return Boolean(
    syncedState.memoryId &&
      currentIdentity.memoryId &&
      syncedState.memoryId === currentIdentity.memoryId &&
      syncMatchesConversationIdentity(syncedState, currentIdentity),
  );
}

function writeChatShellSyncState(
  storage: Storage | undefined,
  state: ChatShellSyncState,
): void {
  if (!storage) return;
  try {
    storage.setItem(CHAT_SHELL_SYNC_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Embedded host pages and privacy modes can block storage. Shell sync is best-effort.
  }
}

function writeChatConversationSyncState(
  storage: Storage | undefined,
  state: ChatConversationSyncState,
): void {
  if (!storage) return;
  try {
    storage.setItem(CHAT_CONVERSATION_SYNC_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Embedded host pages and privacy modes can block storage. Conversation sync is best-effort.
  }
}

function writeChatMessageSyncState(
  storage: Storage | undefined,
  state: ChatMessageSyncState,
): void {
  if (!storage) return;
  try {
    storage.setItem(CHAT_MESSAGE_SYNC_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Embedded host pages and privacy modes can block storage. Message sync is best-effort.
  }
}

function writeChatPresenceSyncState(
  storage: Storage | undefined,
  state: ChatPresenceSyncState,
): void {
  if (!storage) return;
  try {
    storage.setItem(CHAT_PRESENCE_SYNC_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Embedded host pages and privacy modes can block storage. Presence sync is best-effort.
  }
}

function writeChatUnreadSyncState(
  storage: Storage | undefined,
  state: ChatUnreadSyncState,
): void {
  if (!storage) return;
  try {
    storage.setItem(CHAT_UNREAD_SYNC_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Embedded host pages and privacy modes can block storage. Unread sync is best-effort.
  }
}

export function readPersistedChatSessionIdentity(storage: Storage | undefined): SocketInitialSessionIdentity | undefined {
  const rawValue = storage?.getItem(CHAT_SESSION_IDENTITY_STORAGE_KEY) ?? null;
  const parsed = parseChatSessionIdentitySyncState(rawValue);
  if (!parsed) {
    // Security fix f-6ae2706b: even when the stored entry has no usable identity fields (e.g.
    // a legacy sessionHandle-only entry), scrub the HMAC credential from localStorage so
    // same-origin scripts cannot read and replay it. Remove only the storage key — do NOT
    if (rawValue?.includes('"sessionHandle"')) {
      try {
        storage?.removeItem(CHAT_SESSION_IDENTITY_STORAGE_KEY);
      } catch {
        // Best-effort cleanup; storage-restricted embeds may throw.
      }
    }
    return undefined;
  }
  // Security fix f-6ae2706b: if the stored JSON contained a sessionHandle field (old format),
  // actively rewrite the entry without it so the credential is scrubbed from localStorage on
  // the first read after deploy. Same-origin scripts can no longer replay the handle afterward.
  if (rawValue?.includes('"sessionHandle"')) {
    writePersistedChatSessionIdentity(storage, parsed);
  }
  return {
    ...(parsed.guestId ? { guestId: parsed.guestId } : {}),
    ...(parsed.threadId ? { threadId: parsed.threadId } : {}),
    ...(parsed.memoryId ? { memoryId: parsed.memoryId } : {}),
  };
}

export function writePersistedChatSessionIdentity(
  storage: Storage | undefined,
  identity: Pick<SocketInitialSessionIdentity, "guestId" | "threadId" | "memoryId">,
): void {
  const guestId = cleanSessionIdentityValue(identity.guestId);
  const threadId = cleanSessionIdentityValue(identity.threadId);
  const memoryId = cleanSessionIdentityValue(identity.memoryId);
  if (!guestId && !threadId && !memoryId) return;

  try {
    storage?.setItem(
      CHAT_SESSION_IDENTITY_STORAGE_KEY,
      JSON.stringify({
        version: 1,
        ...(guestId ? { guestId } : {}),
        ...(threadId ? { threadId } : {}),
        ...(memoryId ? { memoryId } : {}),
        updatedAt: Date.now(),
      } satisfies ChatSessionIdentitySyncState),
    );
  } catch {
    // Embedded host pages and privacy modes can block storage. The backend-owned cookie remains separate.
  }
}

function persistChatSessionIdentity(identity: SocketInitialSessionIdentity): void {
  writePersistedChatSessionIdentity(getChatShellSyncStorage(), identity);
}

function persistChatSessionIdentityFromResume(payload: SessionResumePayload): void {
  const storage = getChatShellSyncStorage();
  const guestId = cleanSessionIdentityValue(payload.guest_id);
  const threadId = cleanSessionIdentityValue(payload.thread_id);
  const memoryId = cleanSessionIdentityValue(payload.memory_id);
  if (!guestId && !threadId && !memoryId) return;
  writePersistedChatSessionIdentity(storage, { guestId, threadId, memoryId });
}

function postBroadcastMessage(channelName: string, payload: unknown): void {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    const channel = new BroadcastChannel(channelName);
    channel.postMessage(payload);
    channel.close();
  } catch {
    // BroadcastChannel is best-effort; localStorage storage events remain the fallback.
  }
}

function getChatShellSyncStorage(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const storage = window.localStorage;
    if (
      !storage ||
      typeof storage.getItem !== "function" ||
      typeof storage.setItem !== "function" ||
      typeof storage.removeItem !== "function"
    ) {
      return undefined;
    }
    return storage;
  } catch {
    return undefined;
  }
}

function cleanSessionIdentityValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > CHAT_SESSION_IDENTITY_VALUE_MAX_LENGTH) return undefined;
  return trimmed;
}

function playNotificationSound(src: string): Promise<void> {
  if (typeof Audio === "undefined") return Promise.resolve();

  return new Promise((resolve) => {
    const audio = new Audio(src);
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      audio.removeEventListener("ended", finish);
      audio.removeEventListener("error", finish);
      resolve();
    };

    audio.preload = "auto";
    audio.addEventListener("ended", finish);
    audio.addEventListener("error", finish);
    void audio.play().then(undefined, finish);
  });
}

export type ChatModalLayout = "launcher" | "side-panel";

type ServerShellSyncPublisher = (state: ChatShellSyncState) => void;

type ChatModalProps = {

  /**
   * Direct Socket.IO URL for the Phoebe supervisor backend.
   * The widget has one supported browser transport path: Socket.IO.
   *
   * Example: "https://phoebe-2.st.uopx.io:8003"
   */
  backendUrl?: string;
  /** Opt-in same-origin triage pilot; host hooks are hints, not identity authority. */
  silentWidgetAuth?: boolean;
  initialIdentity?: SocketInitialSessionIdentity;
  qualityProfile?: QualityProfile;
  modelId?: string;
  guestBootstrap?: GuestSessionBootstrapOptions | false;
  /**
   * Segment Analytics.js write key (npm library mode).
   * When provided the widget initialises @segment/analytics-next internally.
   * In IIFE mode this is handled via window.__PHOEBE_CONFIG__.segmentWriteKey instead.
   */
  analyticsWriteKey?: string;
  /**
   * Set to true to disable all Segment analytics calls for this widget instance.
   */
  disableAnalytics?: boolean;
  /**
   * Set to true to include message body text in Live Chat Message Sent and
   * Live Chat Message Received Segment track events.
   * Defaults to false — message content is not tracked by default.
   */
  includeMessageBody?: boolean;
  /**
   * Enables browser-console QA utilities under window.__PHOEBE_DEVTOOLS__.
   * Defaults to false so production embeds do not expose test hooks.
   */
  enableDevtools?: boolean;
  /**
   * Visual shell mode. Defaults to the production launcher/modal behavior.
   * `side-panel` is Phase 2 gated and falls back to `launcher` unless
   * `sidePanelShellEnabled` is true or the OpenFeature/GrowthBook flag
   * `super-phoebe.ui.side-panel.enabled` resolves true.
   */
  layout?: ChatModalLayout;
  /**
   * Phase 2 release flag fallback for the right-rail side-panel shell.
   * Keep false/omitted for Phase 1 so the original chat launcher remains active.
   * Defaults to false (fail-closed). When growthBookClientKey is provided,
   * this prop value is used as the initial/fallback value until the
   * OpenFeature provider resolves the flag.
   */
  sidePanelShellEnabled?: boolean;
  /**
   * IIFE release artifacts use the server-evaluated side-panel flag as their
   * only selector.
   * Library and sandbox/local embeds retain the prop/layout behavior.
   */
  strictSidePanelPolicy?: boolean;
  /**
   * Future enrollment consultation scheduling UX.
   * Defaults to false for Phase 1 because scheduling is not yet available.
   */
  enrollmentConsultationEnabled?: boolean;
  /** Enables Legal-approved canned insights in the thinking state. */
  thinkingInsightsEnabled?: boolean;
  /**
   * Called on each new conversation start with the Segment traits stored by
   * the host page (email, enrollmentStatus, and any other persisted traits).
   * Receives `{}` when no traits are present. Fired before the
   * "Live Chat Conversation Started" track event.
   */
  onTraitsResolved?: (traits: SegmentTraits) => void;
  /**
   * Enable or disable the Genesys live chat feature.
   * Defaults to false for Phase 1 because live-agent entry points are not yet available.
   * When growthBookClientKey is provided,
   * this prop value is used as the initial/fallback value until the
   * OpenFeature provider resolves the flag.
   */
  genesysLiveChatEnabled?: boolean;
  /**
   * Enable or disable the Genesys live chat queue position display.
   * Defaults to false. When live assistance is enabled and growthBookClientKey is provided,
   * this prop value is used as the initial/fallback value until the
   * OpenFeature provider resolves the flag.
   */
  genesysLiveChatQueueDisplayEnabled?: boolean;
  /**
   * GrowthBook client-side SDK key. When provided, the OpenFeature JS Web
   * SDK is initialised with a custom GrowthBook-backed provider and the
   * resolved flag values replace the prop-based defaults once the provider
   * is ready.
   * If omitted, only the prop-based values or local defaults are used.
   */
  growthBookClientKey?: string;
  /**
   * Optional GrowthBook API host override. Defaults to GrowthBook's hosted
   * CDN when omitted. Only used when `growthBookClientKey` is provided.
   */
  growthBookApiHost?: string;
};

// ── Shared modal shell (runtime-agnostic) ─────────────────────────────────────

type ModalContentProps = {
  backendUrl?: string;
  canCancelActiveTurn?: boolean;
  layout?: ChatModalLayout;
  chatState: ChatState;
  isMinimized: boolean;
  unreadCount: number;
  unreadBoundaryMessageId?: string | null;
  enableDevtools?: boolean;
  shellSyncSourceId: string;
  serverShellSyncPublisherRef: MutableRefObject<ServerShellSyncPublisher>;
  onServerShellSyncState: (state: ChatShellSyncState | null) => void;
  onButtonClick: () => void;
  onClose: () => void;
  onMinimize: () => void;
  onReopen: () => void;
  onNewChat?: () => void;
  onEndLiveSession?: () => void;
  onSafetyExit?: () => void;
  onAssistantFeedback?: (payload: Omit<WidgetFeedbackSubmitPayload, "version" | "sourceId" | "updatedAt">) => void;
  onScheduleConsultation?: () => void;
  asyncSuggestions?: Map<string, FollowUpSuggestion[]>;
};

function SidePanelFooter() {
  return (
    <AuiIf condition={(state) => state.thread.isEmpty}>
      <footer className={SIDE_PANEL_FOOTER_CLASS}>
        <ChatHomeViewLegalNotice framed={false} />
      </footer>
    </AuiIf>
  );
}

function ModalShell({
  runtime,
  backendUrl,
  canCancelActiveTurn = true,
  layout = "launcher",
  chatState,
  isMinimized,
  unreadCount,
  unreadBoundaryMessageId = null,
  windowContentState = "ready",
  onButtonClick,
  onMinimize,
  onReopen,
  onNewChat,
  onEndLiveSession,
  onSafetyExit,
  onAssistantFeedback,
  onScheduleConsultation,
  asyncSuggestions,
}: ModalContentProps & { runtime: AssistantRuntime; windowContentState?: WindowContentState }) {
  const [isSidePanelNudgeDismissed, setIsSidePanelNudgeDismissed] = useState(false);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const sidePanelHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const sidePanelRef = useRef<HTMLElement | null>(null);
  const [sidePanelMobileTop, setSidePanelMobileTop] = useState(SIDE_PANEL_MOBILE_HEADER_GAP_PX);
  const { bottomOffsetHeight: hostBottomActionBarHeight } = useHostBottomObstructionHeights(
    layout === "side-panel" && chatState === "chat" && !isMinimized,
  );

  const ensureSidePanelTopLayer = useCallback((options: { reorderIfCovered?: boolean } = {}) => {
    if (layout !== "side-panel") return;
    const sidePanel = sidePanelRef.current;
    if (!sidePanel || typeof sidePanel.showPopover !== "function" || typeof sidePanel.hidePopover !== "function") {
      return;
    }

    const shouldShowPopover = chatState === "chat" && !isMinimized;
    const isPopoverOpen = sidePanel.matches(":popover-open");
    try {
      if (shouldShowPopover && !isPopoverOpen) {
        sidePanel.showPopover();
      } else if (!shouldShowPopover && isPopoverOpen) {
        sidePanel.hidePopover();
      } else if (shouldShowPopover && options.reorderIfCovered) {
        const rect = sidePanel.getBoundingClientRect();
        const hitTarget = document.elementFromPoint(
          Math.min(rect.right - 1, rect.left + 24),
          Math.min(rect.bottom - 1, rect.top + 24),
        );
        if (hitTarget && !sidePanel.contains(hitTarget)) {
          sidePanel.hidePopover();
          sidePanel.showPopover();
        }
      }
    } catch {
      // The fixed/z-index shell remains the fallback on browsers without stable popover state.
    }
  }, [chatState, isMinimized, layout]);

  useEffect(() => {
    ensureSidePanelTopLayer();

    return () => {
      const sidePanel = sidePanelRef.current;
      try {
        if (sidePanel?.matches(":popover-open")) {
          sidePanel.hidePopover();
        }
      } catch {
        // Ignore browser popover cleanup races during unmount.
      }
    };
  }, [ensureSidePanelTopLayer]);

  useEffect(() => {
    if (layout !== "side-panel" || chatState !== "chat" || isMinimized) return;

    return scheduleInitialChatHeadingFocus(
      sidePanelHeadingRef,
      () => layout === "side-panel" && chatState === "chat" && !isMinimized,
    );
  }, [chatState, isMinimized, layout]);

  useEffect(() => {
    if (layout !== "side-panel" || chatState !== "chat" || isMinimized) return;

    let animationFrame = 0;
    const scheduledTimeouts: number[] = [];
    const delayedScrollChecks: number[] = [];
    const scheduleTopLayerCheck = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        ensureSidePanelTopLayer({ reorderIfCovered: true });
      });
    };
    const scheduleScrollTopLayerChecks = () => {
      delayedScrollChecks.forEach((timeout) => window.clearTimeout(timeout));
      delayedScrollChecks.length = 0;
      scheduleTopLayerCheck();
      delayedScrollChecks.push(
        window.setTimeout(scheduleTopLayerCheck, 80),
        window.setTimeout(scheduleTopLayerCheck, 220),
      );
    };

    const captureOptions = { capture: true };
    const passiveCaptureOptions = { capture: true, passive: true };
    window.addEventListener("scroll", scheduleScrollTopLayerChecks, passiveCaptureOptions);
    window.addEventListener("resize", scheduleTopLayerCheck, passiveCaptureOptions);
    document.addEventListener("scroll", scheduleScrollTopLayerChecks, passiveCaptureOptions);
    document.addEventListener("transitionend", scheduleTopLayerCheck, captureOptions);
    document.addEventListener("animationend", scheduleTopLayerCheck, captureOptions);
    scheduledTimeouts.push(
      window.setTimeout(scheduleTopLayerCheck, 0),
      window.setTimeout(scheduleTopLayerCheck, 150),
      window.setTimeout(scheduleTopLayerCheck, 350),
    );

    return () => {
      cancelAnimationFrame(animationFrame);
      scheduledTimeouts.forEach((timeout) => window.clearTimeout(timeout));
      delayedScrollChecks.forEach((timeout) => window.clearTimeout(timeout));
      window.removeEventListener("scroll", scheduleScrollTopLayerChecks, passiveCaptureOptions);
      window.removeEventListener("resize", scheduleTopLayerCheck, passiveCaptureOptions);
      document.removeEventListener("scroll", scheduleScrollTopLayerChecks, passiveCaptureOptions);
      document.removeEventListener("transitionend", scheduleTopLayerCheck, captureOptions);
      document.removeEventListener("animationend", scheduleTopLayerCheck, captureOptions);
    };
  }, [chatState, ensureSidePanelTopLayer, isMinimized, layout]);

  useEffect(() => {
    if (layout !== "side-panel" || chatState !== "chat" || isMinimized || typeof window.matchMedia !== "function") return;

    const mediaQuery = window.matchMedia(`(max-width: ${SIDE_PANEL_MOBILE_BREAKPOINT_PX}px)`);
    const syncMobileTop = () => {
      const panel = sidePanelRef.current;
      if (!panel || !mediaQuery.matches) return;
      setSidePanelMobileTop(mobileSidePanelTop(panel));
    };

    syncMobileTop();
    mediaQuery.addEventListener("change", syncMobileTop);
    window.addEventListener("resize", syncMobileTop);
    window.addEventListener("scroll", syncMobileTop, { passive: true });

    return () => {
      mediaQuery.removeEventListener("change", syncMobileTop);
      window.removeEventListener("resize", syncMobileTop);
      window.removeEventListener("scroll", syncMobileTop);
    };
  }, [chatState, isMinimized, layout]);

  useEffect(() => {
    if (layout !== "side-panel" || chatState !== "chat" || isMinimized || typeof window.matchMedia !== "function") return;

    const mediaQuery = window.matchMedia(`(max-width: ${SIDE_PANEL_MOBILE_BREAKPOINT_PX}px)`);
    const closeOnMobileOutsidePointer = (event: PointerEvent) => {
      if (!mediaQuery.matches || event.button !== 0) return;
      const panel = sidePanelRef.current;
      const target = event.target;
      if (!panel || !(target instanceof Node) || panel.contains(target)) return;
      if (target instanceof Element && target.closest("[data-radix-portal], [data-radix-popper-content-wrapper]")) return;

      event.preventDefault();
      event.stopPropagation();
      onMinimize();
      window.requestAnimationFrame(() => launcherRef.current?.focus());
    };

    document.addEventListener("pointerdown", closeOnMobileOutsidePointer, true);
    return () => document.removeEventListener("pointerdown", closeOnMobileOutsidePointer, true);
  }, [chatState, isMinimized, layout, onMinimize]);

  if (layout === "side-panel") {
    const isContentHidden = chatState !== "chat" || isMinimized;
    const reopenSidePanel = isMinimized ? onReopen : onButtonClick;
    const handleSidePanelMinimize = () => {
      onMinimize();
      requestAnimationFrame(() => {
        launcherRef.current?.focus();
      });
    };

    return (
      <AssistantRuntimeProvider runtime={runtime}>
        <TooltipProvider disableHoverableContent>
          {isContentHidden && (
            <div className="aui-root aui-modal-anchor aui-chat-launcher-anchor z-[2147483647]">
              {!isSidePanelNudgeDismissed && (
                <LauncherNudge
                  onDismiss={() => setIsSidePanelNudgeDismissed(true)}
                  onOpen={reopenSidePanel}
                />
              )}
              <span className="aui-chat-launcher-button-slot">
                <AssistantModalButton
                  ref={launcherRef}
                  data-state={isMinimized ? "minimized" : "closed"}
                  tooltip="Open chat"
                  showTooltip={false}
                  aria-label="Open chat"
                  onClick={reopenSidePanel}
                />
                <AssistantLauncherUnreadBadge unreadCount={unreadCount} />
              </span>
            </div>
          )}
          <aside
            ref={sidePanelRef}
            className={cn(
              "aui-root aui-chat-shell aui-side-panel fixed top-0 right-0 bottom-0 z-[2147483647] box-border flex h-[100dvh] max-h-[100dvh] w-[446px] min-w-[446px] max-w-[446px] flex-none flex-col items-start overflow-x-hidden border-l border-[rgba(0,24,35,0.12)] bg-white text-[#001823]",
              isContentHidden && "invisible pointer-events-none",
            )}
            style={{
              "--aui-host-bottom-action-bar-height": `${hostBottomActionBarHeight}px`,
              "--aui-side-panel-mobile-top": `${sidePanelMobileTop}px`,
            } as React.CSSProperties}
            aria-label="Chat preview"
            aria-hidden={isContentHidden || undefined}
            data-testid="aichat_window"
            data-layout="side-panel"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...({ popover: "manual" } as React.HTMLAttributes<HTMLElement>)}
            {...(isContentHidden ? { inert: "" as any } : {})}
          >
            {!isContentHidden && (
              <ThreadHeader
                dismissAction="close"
                headingRef={sidePanelHeadingRef}
                onMinimize={handleSidePanelMinimize}
                onNewChat={onNewChat}
                onScheduleConsultation={onScheduleConsultation}
              />
            )}
            <div className="aui-side-panel-body flex min-h-0 w-full flex-[1_0_0] flex-col items-center justify-center gap-[40px] self-stretch">
              {isContentHidden ? null : windowContentState === "loading" ? (
                <WindowLevelLoader />
              ) : windowContentState === "error" ? (
                <WindowLevelError />
              ) : (
                <Thread
                  backendUrl={backendUrl}
                  canCancelActiveTurn={canCancelActiveTurn}
                  isVisible={chatState === "chat"}
                  unreadBoundaryMessageId={unreadBoundaryMessageId}
                  homeLegalNoticePlacement="none"
                  showHeader={false}
                  onMinimize={handleSidePanelMinimize}
                  onNewChat={onNewChat}
                  onEndLiveSession={onEndLiveSession}
                  onSafetyExit={onSafetyExit}
                  onAssistantFeedback={onAssistantFeedback}
                  onScheduleConsultation={onScheduleConsultation}
                  asyncSuggestions={asyncSuggestions}
                />
              )}
            </div>
            {!isContentHidden && <SidePanelFooter />}
          </aside>
        </TooltipProvider>
      </AssistantRuntimeProvider>
    );
  }

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <TooltipProvider disableHoverableContent>
        <AssistantModal
          backendUrl={backendUrl}
          canCancelActiveTurn={canCancelActiveTurn}
          chatState={chatState}
          isMinimized={isMinimized}
          unreadCount={unreadCount}
          windowContentState={windowContentState}
          unreadBoundaryMessageId={unreadBoundaryMessageId}
          onButtonClick={onButtonClick}
          onMinimize={onMinimize}
          onReopen={onReopen}
          onNewChat={onNewChat}
          onEndLiveSession={onEndLiveSession}
          onSafetyExit={onSafetyExit}
          onAssistantFeedback={onAssistantFeedback}
          onScheduleConsultation={onScheduleConsultation}
          asyncSuggestions={asyncSuggestions}
        />
      </TooltipProvider>
    </AssistantRuntimeProvider>
  );
}

// ── Socket.IO runtime sub-component ──────────────────────────────────────────
//
// Kept as a separate component so hooks are always called unconditionally.

function SocketIORuntime({
  backendUrl,
  initialIdentity,
  qualityProfile,
  modelId,
  guestBootstrap,
  includeMessageBody = false,
  onTraitsResolved,
  layout = "launcher",
  ...rest
}: ModalContentProps &
  Pick<
    ChatModalProps,
    | "backendUrl"
    | "initialIdentity"
    | "qualityProfile"
    | "modelId"
    | "guestBootstrap"
    | "includeMessageBody"
    | "onTraitsResolved"
    | "layout"
  >) {
  const hydratedThreadRef = useRef<string | null>(null);
  // When true, the next session.resume event is suppressed — used after
  // "Start a new chat" so the backend's auto-resume on reconnect does not
  // repopulate the thread with old history.
  const suppressNextResumeRef = useRef(false);
  const pendingHandoffMarkerRef = useRef<ThreadMessageLike | null>(null);
  const pendingLiveMessagesRef = useRef<ThreadMessageLike[]>([]);
  const messagesRef = useRef<ThreadMessageLike[]>([]);
  const activeRunClientMessageIdRef = useRef<string | null>(null);
  const activeRunsRef = useRef<Map<string, ActiveRunState>>(new Map());
  const passiveRunsRef = useRef<Map<string, PassiveRunState>>(new Map());
  const notificationSoundQueueRef = useRef<Promise<void>>(Promise.resolve());
  const playedSentMessageIdsRef = useRef<Set<string>>(new Set());
  const playedReceivedMessageIdsRef = useRef<Set<string>>(new Set());
  const localClientMessageIdsRef = useRef<Set<string>>(new Set());
  const appliedPersistedTurnTraceIdsRef = useRef<Set<string>>(new Set());
  const pendingLocalPersistedTurnsRef = useRef<Map<string, SessionTurnPersistedPayload>>(new Map());
  const discardPersistedTurnsRef = useRef(false);
  const conversationEpochRef = useRef(0);
  const conversationSyncSourceIdRef = useRef(createSyncSourceId());
  const lastAppliedConversationSyncAtRef = useRef(0);
  const messageSyncSourceIdRef = useRef(createSyncSourceId());
  const lastAppliedMessageSyncAtRef = useRef(0);
  const presenceSyncSourceIdRef = useRef(createSyncSourceId());
  const lastAppliedPresenceSyncAtRef = useRef(0);
  const unreadSyncSourceIdRef = useRef(createSyncSourceId());
  const lastAppliedUnreadSyncAtRef = useRef(0);
  const trustedUnreadIdentityRef = useRef<Pick<ChatUnreadSyncState, "guestId" | "threadId" | "memoryId"> | null>(null);
  const replaceMessagesOnNextResumeRef = useRef(false);
  const retiredResumeIdentityRef = useRef<Pick<ChatUnreadSyncState, "guestId" | "threadId" | "memoryId"> | null>(null);
  const scopeAdoptionPendingRef = useRef(false);
  const [scopeAdoptionPending, setScopeAdoptionPendingState] = useState(false);
  const setScopeAdoptionPending = useCallback((pending: boolean) => {
    scopeAdoptionPendingRef.current = pending;
    setScopeAdoptionPendingState(pending);
  }, []);
  const composerSubmission = useMemo(() => ({
    pending: scopeAdoptionPending,
    canSubmit: () => !scopeAdoptionPendingRef.current,
  }), [scopeAdoptionPending]);
  const unreadCountRef = useRef(0);
  const unreadBoundaryMessageIdRef = useRef<string | null>(null);
  const pendingUnreadBoundaryCountRef = useRef(0);
  const unreadMessageIdsRef = useRef<Set<string>>(new Set());
  const isMinimizedRef = useRef(rest.isMinimized);
  const documentFocusedRef = useRef(typeof document === "undefined" ? true : !document.hidden);
  const [messages, setMessages] = useState<ThreadMessageLike[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isPassiveTurnRunning, setIsPassiveTurnRunning] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadBoundaryMessageId, setUnreadBoundaryMessageId] = useState<string | null>(null);
  const [windowContentState, setWindowContentState] = useState<WindowContentState>("ready");
  // Devtools-only override of the window shell state. Null in every normal
  // session; when set, the connect effect defers to it so QA can hold the
  // loader or error on screen long enough to inspect it (VIAF-455).
  const forcedWindowContentStateRef = useRef<WindowContentState | null>(null);
  const applyWindowContentState = (state: WindowContentState) => {
    setWindowContentState(forcedWindowContentStateRef.current ?? state);
  };
  const [hasLearnedConversationIdentity, setHasLearnedConversationIdentity] = useState(false);
  // Ref-backed mirror of isRunning so the reopen guard can read it without
  // adding isRunning to useEffect dep arrays (which would cause spurious re-runs).
  const isRunningRef = useRef(false);
  // Ref-backed mirror of chatState so the controller useMemo closure always
  // reads the latest value without chatState being in the dep array (which
  // would recreate the controller — and disconnect the socket — on every state change).
  const chatStateRef = useRef(rest.chatState);
  useEffect(() => {
    chatStateRef.current = rest.chatState;
  });
  useEffect(() => {
    isMinimizedRef.current = rest.isMinimized;
  });
  // Tracks the previous isMinimized value so the reopen effect only fires on
  // true→false transitions, not on initial mount or unrelated renders.
  const prevIsMinimizedRef = useRef(rest.isMinimized);
  const prevChatStateRef = useRef(rest.chatState);
  const connectSequenceRef = useRef(0);
  const onButtonClickRef = useRef(rest.onButtonClick);
  const onServerShellSyncStateRef = useRef(rest.onServerShellSyncState);
  const applyConversationSyncStateRef = useRef<(syncedState: ChatConversationSyncState | null) => void>(() => undefined);
  const applyMessageSyncStateRef = useRef<(syncedState: ChatMessageSyncState | null) => void>(() => undefined);
  const applyPresenceSyncStateRef = useRef<(syncedState: ChatPresenceSyncState | null) => void>(() => undefined);
  const applyUnreadSyncStateRef = useRef<(syncedState: ChatUnreadSyncState | null) => void>(() => undefined);
  const revalidatePersistedUnreadSyncStateRef = useRef<() => void>(() => undefined);
  const publishMessageSyncStateRef = useRef<
    (message: { clientMessageId: string; text: string; createdAt: number }) => void
  >(() => undefined);
  const publishPresenceSyncStateRef = useRef<(action: ChatPresenceSyncAction, active: boolean) => void>(() => undefined);
  const publishUnreadSyncStateRef = useRef<(nextUnreadCount: number) => void>(() => undefined);

  useEffect(() => {
    onServerShellSyncStateRef.current = rest.onServerShellSyncState;
    onButtonClickRef.current = rest.onButtonClick;
  });

  const setThreadMessages = (next: ThreadMessageLike[] | ((current: ThreadMessageLike[]) => ThreadMessageLike[])) => {
    const resolved = typeof next === "function" ? next(messagesRef.current) : next;
    messagesRef.current = resolved;
    setMessages(resolved);
  };

  const enqueueNotificationSound = (src: string) => {
    notificationSoundQueueRef.current = notificationSoundQueueRef.current
      .catch(() => undefined)
      .then(() => playNotificationSound(src));
  };

  const playSentMessageSoundOnce = (messageId: string | null | undefined) => {
    if (!messageId || playedSentMessageIdsRef.current.has(messageId)) return;
    playedSentMessageIdsRef.current.add(messageId);
    enqueueNotificationSound(USER_MESSAGE_SENT_SOUND_SRC);
  };

  const playReceivedMessageSoundOnce = (messageId: string | null | undefined) => {
    if (!messageId || playedReceivedMessageIdsRef.current.has(messageId)) return;
    playedReceivedMessageIdsRef.current.add(messageId);
    void claimReceivedNotificationSound(messageId).then((claimed) => {
      if (claimed) {
        enqueueNotificationSound(USER_MESSAGE_RECEIVED_SOUND_SRC);
      }
    });
  };

  const queueLiveMessage = (message: ThreadMessageLike) => {
    pendingLiveMessagesRef.current = appendThreadMessageIfNew(pendingLiveMessagesRef.current, message);
  };

  const flushPendingLiveTranscript = (force = false) => {
    // Skip flush if there are active streaming responses, unless force=true for
    // critical messages (ended, error) that must appear immediately.
    if (!force && (activeRunsRef.current.size > 0 || passiveRunsRef.current.size > 0)) return;
    const marker = pendingHandoffMarkerRef.current;
    const pendingMessages = pendingLiveMessagesRef.current;
    if (!marker && pendingMessages.length === 0) return;

    pendingHandoffMarkerRef.current = null;
    pendingLiveMessagesRef.current = [];
    setThreadMessages((current) => {
      const withMarker = marker ? upsertThreadMessage(current, marker) : current;
      return pendingMessages.reduce(appendThreadMessageIfNew, withMarker);
    });
  };

  const startRun = () => {
    if (activeRunsRef.current.size > 0) {
      conversationEpochRef.current += 1;
      activeRunsRef.current.forEach(({ controller }) => controller.abort());
      activeRunsRef.current.clear();
      activeRunClientMessageIdRef.current = null;
    }
    isRunningRef.current = true;
    setIsRunning(true);
    setIsPassiveTurnRunning(false);
    return conversationEpochRef.current;
  };

  const syncRunningState = () => {
    const hasLocalRun = activeRunsRef.current.size > 0;
    const hasPassiveRun = passiveRunsRef.current.size > 0;
    const nextIsRunning = hasLocalRun || hasPassiveRun;
    isRunningRef.current = nextIsRunning;
    setIsRunning(nextIsRunning);
    setIsPassiveTurnRunning(hasPassiveRun && !hasLocalRun);
  };

  const finishRun = (clientMessageId: string, abortController: AbortController) => {
    const activeRun = activeRunsRef.current.get(clientMessageId);
    if (activeRun?.controller === abortController) {
      activeRunsRef.current.delete(clientMessageId);
    }
    if (activeRunClientMessageIdRef.current === clientMessageId) {
      activeRunClientMessageIdRef.current = activeRunsRef.current.keys().next().value ?? null;
    }
    syncRunningState();
  };

  const reconcilePendingLocalPersistedTurn = (clientMessageId: string, assistantMessageId?: string) => {
    const persistedTurn = pendingLocalPersistedTurnsRef.current.get(clientMessageId);
    if (persistedTurn && reconcileLocalPersistedTurn(persistedTurn, assistantMessageId)) {
      pendingLocalPersistedTurnsRef.current.delete(clientMessageId);
    }
  };

  // Repair pass for turns the live path broke (lost chunks/final or a socket drop that also
  // swallowed session.turn.persisted). The backend re-sends the canonical turns with every
  // session.resume; converge any completed local turn whose rendered text diverges from the
  // saved answer. Idempotent: once a row matches the persisted content it is left untouched.
  const reconcileLocalTurnsFromResume = (turns: SessionResumePayload["turns"]) => {
    for (const turn of turns ?? []) {
      const clientMessageId = cleanSessionIdentityValue(turn?.client_message_id);
      if (!clientMessageId || !turn?.trace_id) continue;
      if (!localClientMessageIdsRef.current.has(clientMessageId)) continue;
      if (activeRunsRef.current.has(clientMessageId)) continue;
      const persistedAssistant = resumeTurnsToThreadMessages([turn]).find(
        (message) => message.role === "assistant",
      );
      if (!persistedAssistant) continue;
      const renderedAssistant = messagesRef.current.find(
        (message) => message.role === "assistant" && getThreadMessageClientMessageId(message) === clientMessageId,
      );
      if (
        renderedAssistant &&
        getContentText(renderedAssistant.content).trim() === getContentText(persistedAssistant.content).trim()
      ) {
        pendingLocalPersistedTurnsRef.current.delete(clientMessageId);
        continue;
      }
      if (reconcileLocalPersistedTurn({ client_message_id: clientMessageId, turn }, renderedAssistant?.id)) {
        appliedPersistedTurnTraceIdsRef.current.add(turn.trace_id);
        pendingLocalPersistedTurnsRef.current.delete(clientMessageId);
      }
    }
  };

  const payloadMatchesTrustedMemory = (payload: { memory_id?: string }) => {
    const trustedMemoryId = trustedUnreadIdentityRef.current?.memoryId;
    const payloadMemoryId = cleanSessionIdentityValue(payload.memory_id);
    return Boolean(trustedMemoryId && payloadMemoryId === trustedMemoryId);
  };

  const beginPassiveRun = (payload: SupervisorRoutingStartedPayload | SupervisorStageUpdatedPayload) => {
    const clientMessageId = cleanSessionIdentityValue(payload.client_message_id);
    if (!clientMessageId || localClientMessageIdsRef.current.has(clientMessageId)) return null;
    if (!payloadMatchesTrustedMemory(payload)) return null;

    let passiveRun = passiveRunsRef.current.get(clientMessageId);
    if (!passiveRun) {
      passiveRun = {
        assistantCreatedAt: new Date(),
        assistantMessageId: `passive-assistant-${clientMessageId}`,
        fullText: "",
      };
      passiveRunsRef.current.set(clientMessageId, passiveRun);
      socketStatusActions.startTurn(clientMessageId);
      setThreadMessages((current) => upsertThreadMessage(current, createRunningAssistantMessage(passiveRun!.assistantMessageId)));
      syncRunningState();
    }
    return { clientMessageId, passiveRun };
  };

  const finishPassiveRun = (clientMessageId: string) => {
    if (!passiveRunsRef.current.delete(clientMessageId)) return;
    socketStatusActions.finishTurn(clientMessageId);
    syncRunningState();
  };

  const applyPassiveChunk = (payload: SupervisorResponseChunkPayload) => {
    const chunk = payload.chunk ? sanitizeSupervisorResponseChunk(payload.chunk) : "";
    if (!chunk) return;
    const active = beginPassiveRun(payload);
    if (!active) return;
    active.passiveRun.fullText += chunk;
    setThreadMessages((current) => upsertThreadMessage(current, {
      id: active.passiveRun.assistantMessageId,
      role: "assistant",
      content: [{ type: "text", text: active.passiveRun.fullText }],
      createdAt: active.passiveRun.assistantCreatedAt,
      status: { type: "running" },
      metadata: { custom: { clientMessageId: active.clientMessageId, syncedFrom: "supervisor.response.chunk" } },
    }));
  };

  const applyPassiveFinal = (payload: SupervisorResponseFinalPayload) => {
    const clientMessageId = cleanSessionIdentityValue(payload.client_message_id);
    if (!clientMessageId || localClientMessageIdsRef.current.has(clientMessageId)) return;
    if (!payloadMatchesTrustedMemory(payload)) return;
    const passiveRun = passiveRunsRef.current.get(clientMessageId);
    if (!passiveRun) return;
    const finalText = sanitizeSupervisorResponseChunk(
      payload.response?.text ?? payload.text ?? passiveRun.fullText,
    ).trim();
    if (finalText) {
      setThreadMessages((current) => upsertThreadMessage(current, {
        id: passiveRun.assistantMessageId,
        role: "assistant",
        content: [{ type: "text", text: finalText }],
        createdAt: passiveRun.assistantCreatedAt,
        status: { type: "complete", reason: "unknown" },
        metadata: { custom: { clientMessageId, syncedFrom: "supervisor.response.final" } },
      }));
    } else {
      setThreadMessages((current) => current.filter((message) => message.id !== passiveRun.assistantMessageId));
    }
    finishPassiveRun(clientMessageId);
  };

  const applyPassiveError = (payload: SupervisorResponseErrorPayload) => {
    const clientMessageId = cleanSessionIdentityValue(payload.client_message_id);
    if (!clientMessageId || localClientMessageIdsRef.current.has(clientMessageId)) return;
    if (!payloadMatchesTrustedMemory(payload)) return;
    const passiveRun = passiveRunsRef.current.get(clientMessageId);
    if (!passiveRun) return;
    setThreadMessages((current) => upsertThreadMessage(current, {
      id: passiveRun.assistantMessageId,
      role: "assistant",
      content: [{ type: "text", text: passiveRun.fullText }],
      createdAt: passiveRun.assistantCreatedAt,
      status: { type: "incomplete", reason: "error", error: payload.error ?? "Backend error" },
      metadata: { custom: { clientMessageId, syncedFrom: "supervisor.response.error" } },
    }));
    socketStatusActions.failTurn(payload);
    finishPassiveRun(clientMessageId);
  };

  // thread_id from session.resume — used as conversation_id in track events.
  const [threadId, setThreadId] = useState<string | null>(null);
  const [asyncSuggestions, setAsyncSuggestions] = useState<Map<string, FollowUpSuggestion[]>>(new Map());

  const onSuggestionsReceived = useCallback(
    (clientMessageId: string, suggestions: FollowUpSuggestion[]) => {
      setAsyncSuggestions((prev) => new Map(prev).set(clientMessageId, suggestions));
    },
    [],
  );

  // Timestamp (ms) when the conversation transitioned to "chat" state.
  // Used to compute conversation_duration for all track events.
  const conversationStartedAtRef = useRef<number | null>(null);

  // Ref-backed mirror of threadId state for stale-closure-safe access in
  // handleClose and other non-reactive contexts.
  const threadIdRef = useRef<string | null>(null);

  // Once-per-session boolean guard: Conversation Started fires exactly
  // once even if threadId updates independently after session.resume.
  const conversationStartedRef = useRef<boolean>(false);

  // Ref-backed mirror of includeMessageBody prop so useMemo controller closures
  // always read the latest value without needing to be in the dep array.
  const includeMessageBodyRef = useRef<boolean>(includeMessageBody);
  useEffect(() => {
    includeMessageBodyRef.current = includeMessageBody;
  });

  // Stable ref for onTraitsResolved so the conversation-start effect does not
  // re-fire if the callback reference changes between renders.
  const onTraitsResolvedRef = useRef<((traits: SegmentTraits) => void) | undefined>(onTraitsResolved);
  useEffect(() => {
    onTraitsResolvedRef.current = onTraitsResolved;
  });

  // Helper: elapsed seconds since conversation started (0 if not yet started).
  const getConversationDuration = () => {
    if (!conversationStartedAtRef.current) return 0;
    return Math.round((Date.now() - conversationStartedAtRef.current) / 1000);
  };

  const isWidgetAttentionActive = () =>
    chatStateRef.current === "chat" && !isMinimizedRef.current && documentFocusedRef.current;

  const setUnreadBoundaryIfUnset = (messageId: string | null | undefined) => {
    if (!messageId || unreadBoundaryMessageIdRef.current) return;
    unreadBoundaryMessageIdRef.current = messageId;
    setUnreadBoundaryMessageId(messageId);
  };

  const clearUnreadBoundary = () => {
    if (
      !unreadBoundaryMessageIdRef.current &&
      unreadMessageIdsRef.current.size === 0 &&
      pendingUnreadBoundaryCountRef.current === 0
    ) {
      return;
    }
    unreadBoundaryMessageIdRef.current = null;
    pendingUnreadBoundaryCountRef.current = 0;
    unreadMessageIdsRef.current.clear();
    setUnreadBoundaryMessageId(null);
  };

  const setSyncedUnreadCount = (nextUnreadCount: number) => {
    const sanitizedCount = Math.max(0, Math.min(99, Math.floor(nextUnreadCount)));
    if (sanitizedCount === 0) clearUnreadBoundary();
    else pendingUnreadBoundaryCountRef.current = sanitizedCount;
    if (unreadCountRef.current === sanitizedCount) return;
    unreadCountRef.current = sanitizedCount;
    setUnreadCount(sanitizedCount);
  };

  const markMessageUnreadIfNeeded = (messageId: string | null | undefined) => {
    const stableMessageId = messageId?.trim();
    if (!stableMessageId) return;
    if (isWidgetAttentionActive()) {
      clearUnreadBoundary();
      publishUnreadSyncStateRef.current(0);
      return;
    }
    if (unreadMessageIdsRef.current.has(stableMessageId)) return;
    unreadMessageIdsRef.current.add(stableMessageId);
    setUnreadBoundaryIfUnset(stableMessageId);
    publishUnreadSyncStateRef.current(Math.min(99, unreadCountRef.current + 1));
  };

  const messageIdAtUnreadBoundary = (threadMessages: readonly ThreadMessageLike[], nextUnreadCount: number) => {
    const incomingMessages = threadMessages.filter((message) => message.role === "assistant");
    const boundedCount = Math.max(0, Math.min(incomingMessages.length, Math.floor(nextUnreadCount)));
    if (boundedCount === 0) return null;
    return incomingMessages[incomingMessages.length - boundedCount]?.id ?? null;
  };
  const reconcileLocalPersistedTurn = (
    payload: SessionTurnPersistedPayload,
    explicitAssistantMessageId?: string,
  ) => {
    const clientMessageId = cleanSessionIdentityValue(payload.client_message_id);
    const turn = payload.turn;
    if (!clientMessageId || !turn) return false;

    const persistedAssistant = resumeTurnsToThreadMessages([turn]).find((message) => message.role === "assistant");
    if (!persistedAssistant) return false;
    const activeAssistantId = activeRunsRef.current.get(clientMessageId)?.assistantMessageId;
    const renderedAssistantId = messagesRef.current.find(
      (message) => message.role === "assistant" && getThreadMessageClientMessageId(message) === clientMessageId,
    )?.id;
    // Fall back to the persisted row's own deterministic id (`<trace>-assistant`) when the
    // live row cannot be located (e.g. it was removed by the empty-run cleanup path): the
    // canonical saved answer must always land without a page refresh.
    const assistantMessageId =
      explicitAssistantMessageId ?? activeAssistantId ?? renderedAssistantId ?? persistedAssistant.id;

    setThreadMessages((current) => upsertThreadMessage(current, {
      ...persistedAssistant,
      id: assistantMessageId,
      status: { type: "complete", reason: "unknown" },
    }));
    return true;
  };
  const applyRemoteTurnPersisted = (payload: SessionTurnPersistedPayload) => {
    const turn = payload.turn;
    if (!turn?.trace_id) return;
    const clientMessageId = payload.client_message_id;
    if (clientMessageId && localClientMessageIdsRef.current.has(clientMessageId)) {
      appliedPersistedTurnTraceIdsRef.current.add(turn.trace_id);
      pendingLocalPersistedTurnsRef.current.set(clientMessageId, payload);
      if (!activeRunsRef.current.has(clientMessageId) && reconcileLocalPersistedTurn(payload)) {
        pendingLocalPersistedTurnsRef.current.delete(clientMessageId);
      }
      return;
    }
    if (!payloadMatchesTrustedMemory(payload)) return;
    if (clientMessageId) finishPassiveRun(clientMessageId);
    if (appliedPersistedTurnTraceIdsRef.current.has(turn.trace_id)) return;

    // Discard fence: if clearConversationState() ran (safety exit, new chat), reject persisted
    // turns until the next session.resume is applied — prevents late async AgentCore memory
    // writes from repopulating a cleared thread.
    if (discardPersistedTurnsRef.current) return;

    appliedPersistedTurnTraceIdsRef.current.add(turn.trace_id);
    const remoteMessages = resumeTurnsToThreadMessages([turn]);
    let firstNewUnreadMessageId: string | null = null;
    setThreadMessages((current) => {
      const withoutSyncedOptimisticUser = clientMessageId
        ? current.filter(
            (message) =>
              message.id !== `user-${clientMessageId}` && message.id !== `passive-assistant-${clientMessageId}`,
          )
        : current;
      if (!isWidgetAttentionActive()) {
        firstNewUnreadMessageId =
          remoteMessages.find(
            (remoteMessage) =>
              remoteMessage.role === "assistant" &&
              remoteMessage.id &&
              !withoutSyncedOptimisticUser.some((message) => message.id === remoteMessage.id),
          )?.id ?? null;
      }
      return remoteMessages.reduce(appendThreadMessageIfNew, withoutSyncedOptimisticUser);
    });
    const assistantMessage = remoteMessages.find((message) => message.role === "assistant");
    playReceivedMessageSoundOnce(
      receivedMessageSoundKey({
        traceId: turn.trace_id ?? assistantMessage?.id,
        threadId: turn.thread_id,
        response: turn.assistant_response,
      }),
    );

    if (isWidgetAttentionActive()) {
      clearUnreadBoundary();
      publishUnreadSyncStateRef.current(0);
      return;
    }
    if (firstNewUnreadMessageId) {
      markMessageUnreadIfNeeded(firstNewUnreadMessageId);
    }
  };

  const applyRemoteMessageSync = (syncedState: ChatMessageSyncState | null) => {
    if (!syncedState) return;
    if (syncedState.sourceId === messageSyncSourceIdRef.current) return;
    if (syncedState.updatedAt <= lastAppliedMessageSyncAtRef.current) return;
    const trustedIdentity = trustedUnreadIdentityRef.current;
    if (!trustedIdentity || !syncMatchesConversationIdentity(syncedState, trustedIdentity)) return;

    lastAppliedMessageSyncAtRef.current = syncedState.updatedAt;
    const messageId = `user-${syncedState.clientMessageId}`;
    setThreadMessages((current) => {
      const nextMessage: ThreadMessageLike = {
        id: messageId,
        role: "user",
        content: [{ type: "text", text: syncedState.text }],
        createdAt: new Date(syncedState.createdAt),
        metadata: {
          custom: {
            clientMessageId: syncedState.clientMessageId,
            syncedFrom: "widget.message.sync",
          },
        },
      };
      if (hasThreadMessageClientId(current, syncedState.clientMessageId)) return current;
      const passiveMessageId = passiveRunsRef.current.get(syncedState.clientMessageId)?.assistantMessageId;
      const passiveMessageIndex = passiveMessageId
        ? current.findIndex((message) => message.id === passiveMessageId)
        : -1;
      if (passiveMessageIndex < 0) return [...current, nextMessage];
      return [...current.slice(0, passiveMessageIndex), nextMessage, ...current.slice(passiveMessageIndex)];
    });
  };

  const persistedInitialIdentity = useMemo(
    () => readPersistedChatSessionIdentity(getChatShellSyncStorage()),
    [],
  );
  // Apply any pending safety-exit override so library consumers (not just IIFE)
  // get a fresh identity after a safety exit across reloads. Explicit host
  // identity wins over browser-persisted guest continuity.
  const safetyExitIdentityOverride = useMemo(
    () =>
      resolveSafetyExitSessionIdentityOverride(
        persistedInitialIdentity || initialIdentity ? { ...persistedInitialIdentity, ...initialIdentity } : undefined,
      ),
    [initialIdentity, persistedInitialIdentity],
  );
  const effectiveInitialIdentity = safetyExitIdentityOverride.initialIdentity;
  const resetGuestOnNextBootstrap = safetyExitIdentityOverride.resetGuestOnNextBootstrap;
  const pendingSafetyExitResetRef = useRef(resetGuestOnNextBootstrap);
  useEffect(() => {
    pendingSafetyExitResetRef.current = resetGuestOnNextBootstrap;
  }, [resetGuestOnNextBootstrap]);
  // Tokens are credentials, not conversation identity. Authenticated embeds may
  // pass userToken; guest credentials stay server-owned behind the HttpOnly cookie.
  const hasConfiguredConversationIdentity = Boolean(
    effectiveInitialIdentity?.guestId ||
      effectiveInitialIdentity?.threadId ||
      effectiveInitialIdentity?.memoryId ||
      effectiveInitialIdentity?.userToken,
  );
  const hasKnownConversationIdentity = hasConfiguredConversationIdentity || hasLearnedConversationIdentity;

  const controller = useMemo(
    () =>
      createSocketChatRuntimeController({
        backendUrl,
        initialIdentity: effectiveInitialIdentity,
        qualityProfile,
        modelId,
        guestBootstrap,
        resetGuestOnNextBootstrap,
        shouldAcceptSessionResume: (payload: SessionResumePayload) => {
          const threadId = cleanSessionIdentityValue(payload.thread_id);
          const memoryId = cleanSessionIdentityValue(payload.memory_id);
          const retiredIdentity = retiredResumeIdentityRef.current;
          if (
            retiredIdentity &&
            (retiredIdentity.threadId === threadId || retiredIdentity.memoryId === memoryId)
          ) return false;
          if (!replaceMessagesOnNextResumeRef.current) return true;
          return Boolean(
            threadId &&
            memoryId,
          );
        },
        onSessionIdentityUpdated: (identity) => {
          persistChatSessionIdentity(identity);
        },
        onSessionResume: (payload: SessionResumePayload) => {
          if (pendingSafetyExitResetRef.current) {
            clearSafetyExitSessionOverride();
            pendingSafetyExitResetRef.current = false;
          }
          const replaceMessages = replaceMessagesOnNextResumeRef.current;
          const threadId = cleanSessionIdentityValue(payload.thread_id);
          const memoryId = cleanSessionIdentityValue(payload.memory_id);
          if (replaceMessages) {
            replaceMessagesOnNextResumeRef.current = false;
            setScopeAdoptionPending(false);
          }
          persistChatSessionIdentityFromResume(payload);
          trustedUnreadIdentityRef.current = memoryId ? {
            guestId: cleanSessionIdentityValue(payload.guest_id),
            threadId,
            memoryId,
          } : null;
          revalidatePersistedUnreadSyncStateRef.current();
          if (payload.guest_id || payload.thread_id || payload.memory_id) {
            setHasLearnedConversationIdentity(true);
          }
          if (chatStateRef.current !== "chat") return;
          // Suppress resume hydration after "Start a new chat" — the backend
          // auto-sends session.resume on every connect; consume and ignore it
          // once so the welcome screen stays clean.
          if (suppressNextResumeRef.current) {
            suppressNextResumeRef.current = false;
            return;
          }
          if (hydratedThreadRef.current === (payload.thread_id ?? "session.resume")) {
            // Already hydrated (e.g. a mid-session reconnect): don't re-merge the transcript,
            // but do repair local turns whose live stream broke before the saved answer landed.
            reconcileLocalTurnsFromResume(payload.turns);
            return;
          }
          discardPersistedTurnsRef.current = false;
          const isThreadSwitch = hydratedThreadRef.current !== null;
          let mergedMessagesForUnreadBoundary: ThreadMessageLike[] = [];
          setThreadMessages((current) => {
            hydratedThreadRef.current = payload.thread_id ?? "session.resume";
            const resumedMessages = resumeTurnsToThreadMessages(payload.turns);
            const marker = liveHandoffToThreadMessage({ live_assistance: payload.live_assistance ?? undefined });
            const resumedWithMarker = marker && !resumedMessages.some((message) => message.id === marker.id)
              ? [...resumedMessages, marker]
              : resumedMessages;
            const mergedMessages = replaceMessages || isThreadSwitch
              ? resumedWithMarker
              : mergeResumedThreadMessages(current, resumedMessages, marker, (clientMessageId) =>
                  activeRunsRef.current.has(clientMessageId),
                );
            mergedMessagesForUnreadBoundary = mergedMessages;
            return mergedMessages;
          });
          setUnreadBoundaryIfUnset(
            messageIdAtUnreadBoundary(
              mergedMessagesForUnreadBoundary,
              pendingUnreadBoundaryCountRef.current || unreadCountRef.current,
            ),
          );
          if (payload.thread_id) setThreadId(payload.thread_id);
        },
        onMessageSent: ({ clientMessageId, threadId: msgThreadId, message }) => {
          localClientMessageIdsRef.current.add(clientMessageId);
          playSentMessageSoundOnce(clientMessageId);
          publishMessageSyncStateRef.current({
            clientMessageId,
            text: message,
            createdAt: Date.now(),
          });
          setAsyncSuggestions(new Map());
          if (msgThreadId) {
            setThreadId(msgThreadId);
          }
          trackUOPX(
            UOPX_EVENT.SEARCH_TEXT,
            "Search Text Value Entered",
            UOPX_LOCATION.SEARCH,
          );
          track("Live Chat Message Sent", {
            conversation_id: msgThreadId ?? undefined,
            message_id: clientMessageId,
            conversation_duration: getConversationDuration(),
            ...(includeMessageBodyRef.current ? { message_body: message } : {}),
          });
        },
        onMessageReceived: ({ traceId, threadId: msgThreadId, agentId, agentName, response }) => {
          playReceivedMessageSoundOnce(receivedMessageSoundKey({ agentId, traceId, threadId: msgThreadId, response }));
          track("Live Chat Message Received", {
            conversation_id: msgThreadId ?? undefined,
            message_id: traceId ?? undefined,
            agent_id: agentId ?? undefined,
            agent_name: agentName ?? undefined,
            conversation_duration: getConversationDuration(),
            ...(includeMessageBodyRef.current && response ? { message_body: response } : {}),
          });
        },
        onSupervisorRoutingStarted: (payload) => {
          beginPassiveRun(payload);
        },
        onSupervisorStageUpdated: (payload) => {
          if (!beginPassiveRun(payload)) return;
          socketStatusActions.updateStage(payload);
        },
        onSupervisorResponseChunk: applyPassiveChunk,
        onSupervisorResponseFinal: applyPassiveFinal,
        onSupervisorResponseError: applyPassiveError,
        onTurnPersisted: (payload) => {
          applyRemoteTurnPersisted(payload);
        },
        onHandoffCompleted: (payload) => {
          const marker = liveHandoffToThreadMessage(payload);
          if (!marker) return;
          markMessageUnreadIfNeeded(marker.id);
          pendingHandoffMarkerRef.current = marker;
          flushPendingLiveTranscript(true); // Force flush to show handoff marker immediately
        },
        onLiveMessage: (payload) => {
          const marker = liveHandoffMarkerFromLiveMessage(payload);
          if (marker) {
            markMessageUnreadIfNeeded(marker.id);
            pendingHandoffMarkerRef.current = marker;
          }
          const message = liveMessageToThreadMessage(payload);
          if (message) {
            playReceivedMessageSoundOnce(
              payload.event_id ??
                payload.provider_message_id ??
                `${payload.live_session_id ?? "live"}:${payload.occurred_at ?? ""}:${payload.text ?? ""}`,
            );
            markMessageUnreadIfNeeded(message.id);
            queueLiveMessage(message);
          }
          flushPendingLiveTranscript();
        },
        onLiveSystemMessage: (payload) => {
          const message = liveSystemMessageToThreadMessage(payload);
          if (message) {
            markMessageUnreadIfNeeded(message.id);
            queueLiveMessage(message);
          }
          flushPendingLiveTranscript();
        },
        onLiveEnded: (payload) => {
          const message = liveEndedToThreadMessage(payload);
          markMessageUnreadIfNeeded(message.id);
          queueLiveMessage(message);
          flushPendingLiveTranscript(true); // Force flush - session is over
        },
        onLiveError: (payload) => {
          const message = liveErrorToThreadMessage(payload);
          markMessageUnreadIfNeeded(message.id);
          queueLiveMessage(message);
          flushPendingLiveTranscript(true); // Force flush - critical error
        },
        onHandoffFailed: () => {
          // The backend emits the sanitized final Phoebe response for this same turn.
          // Avoid appending a second transcript item with provider/internal error text.
        },
        onSuggestionsReceived,
        onWidgetShellSync: (payload) => {
          onServerShellSyncStateRef.current(parseChatShellSyncState(JSON.stringify(payload) ?? null));
        },
        onWidgetConversationSync: (payload) => {
          applyConversationSyncStateRef.current(parseChatConversationSyncState(JSON.stringify(payload) ?? null));
        },
        onWidgetMessageSync: (payload) => {
          applyMessageSyncStateRef.current(parseChatMessageSyncState(JSON.stringify(payload) ?? null));
        },
        onWidgetPresenceSync: (payload) => {
          applyPresenceSyncStateRef.current(parseChatPresenceSyncState(JSON.stringify(payload) ?? null));
        },
        onWidgetUnreadSync: (payload) => {
          applyUnreadSyncStateRef.current(parseChatUnreadSyncState(JSON.stringify(payload) ?? null));
        },
      }),
    [backendUrl, effectiveInitialIdentity, guestBootstrap, modelId, qualityProfile, resetGuestOnNextBootstrap],
  );

  const onAssistantFeedback = useCallback(
    (payload: Omit<WidgetFeedbackSubmitPayload, "version" | "sourceId" | "updatedAt">) => {
      void controller.publishWidgetFeedbackSubmit({
        version: 1,
        eventId: createSyncEventId("feedback"),
        sourceId: messageSyncSourceIdRef.current,
        ...payload,
        updatedAt: Date.now(),
      });
    },
    [controller],
  );

  useEffect(() => {
    publishMessageSyncStateRef.current = ({ clientMessageId, text, createdAt }) => {
      const trimmedText = text.trim();
      if (!trimmedText) return;
      const storage = getChatShellSyncStorage();
      const updatedAt = Math.max(Date.now(), lastAppliedMessageSyncAtRef.current + 1);
      lastAppliedMessageSyncAtRef.current = updatedAt;
      const controllerIdentity = controller.getIdentity();
      const syncState: ChatMessageSyncState = {
        version: 1,
        eventId: createSyncEventId("message"),
        sourceId: messageSyncSourceIdRef.current,
        action: "message_sent",
        ...(controllerIdentity.guestId ? { guestId: controllerIdentity.guestId } : {}),
        ...(controllerIdentity.threadId ?? threadIdRef.current
          ? { threadId: controllerIdentity.threadId ?? threadIdRef.current ?? undefined }
          : {}),
        ...(controllerIdentity.memoryId ? { memoryId: controllerIdentity.memoryId } : {}),
        clientMessageId,
        text: trimmedText,
        createdAt,
        updatedAt,
      };
      writeChatMessageSyncState(storage, syncState);
      postBroadcastMessage(CHAT_MESSAGE_SYNC_CHANNEL, syncState);
      void controller.publishWidgetMessageSync(syncState);
    };

    publishPresenceSyncStateRef.current = (action, active) => {
      const storage = getChatShellSyncStorage();
      const updatedAt = Math.max(Date.now(), lastAppliedPresenceSyncAtRef.current + 1);
      lastAppliedPresenceSyncAtRef.current = updatedAt;
      const syncState: ChatPresenceSyncState = {
        version: 1,
        eventId: createSyncEventId("presence"),
        sourceId: presenceSyncSourceIdRef.current,
        action,
        active,
        updatedAt,
      };
      writeChatPresenceSyncState(storage, syncState);
      postBroadcastMessage(CHAT_PRESENCE_SYNC_CHANNEL, syncState);
      void controller.publishWidgetPresenceSync(syncState);
    };

    publishUnreadSyncStateRef.current = (nextUnreadCount) => {
      const storage = getChatShellSyncStorage();
      const sanitizedCount = Math.max(0, Math.min(99, Math.floor(nextUnreadCount)));
      const updatedAt = Math.max(Date.now(), lastAppliedUnreadSyncAtRef.current + 1);
      lastAppliedUnreadSyncAtRef.current = updatedAt;
      setSyncedUnreadCount(sanitizedCount);
      const trustedIdentity = trustedUnreadIdentityRef.current;
      if (!trustedIdentity) return;
      const syncState: ChatUnreadSyncState = {
        version: 1,
        eventId: createSyncEventId("unread"),
        sourceId: unreadSyncSourceIdRef.current,
        ...(trustedIdentity.guestId ? { guestId: trustedIdentity.guestId } : {}),
        ...(trustedIdentity.threadId ? { threadId: trustedIdentity.threadId } : {}),
        memoryId: trustedIdentity.memoryId,
        unreadCount: sanitizedCount,
        updatedAt,
      };
      writeChatUnreadSyncState(storage, syncState);
      postBroadcastMessage(CHAT_UNREAD_SYNC_CHANNEL, syncState);
      void controller.publishWidgetUnreadSync(syncState);
    };

    return () => {
      publishMessageSyncStateRef.current = () => undefined;
      publishPresenceSyncStateRef.current = () => undefined;
      publishUnreadSyncStateRef.current = () => undefined;
    };
  }, [controller]);

  useEffect(() => {
    const publish: ServerShellSyncPublisher = (state) => {
      void controller.publishWidgetShellSync(state);
    };
    rest.serverShellSyncPublisherRef.current = publish;
    return () => {
      if (rest.serverShellSyncPublisherRef.current === publish) {
        rest.serverShellSyncPublisherRef.current = () => undefined;
      }
    };
  }, [controller, rest.serverShellSyncPublisherRef]);

  // Emit Conversation Started exactly once per session. Re-fires after
  // handleNewChat() resets conversationStartedRef. The guard prevents
  // duplicate fires if threadId state updates again (e.g. a second
  // session.resume arrives with a refreshed thread_id).
  useEffect(() => {
    if (!threadId) return;
    if (conversationStartedRef.current) return;
    conversationStartedRef.current = true;
    const traits = getTraits();
    // TODO(VIAF-94): wire traits to actual usage (e.g. pass to backend, personalise UI).
    onTraitsResolvedRef.current?.(traits);
    track("Live Chat Conversation Started", {
      // Read threadId from state directly — threadIdRef is synced in a separate
      // effect that runs after this one, so it would be stale on the first fire.
      conversation_id: threadId,
      conversation_duration: 0,
    });
  }, [threadId]);

  // Keep threadIdRef in sync with threadId state so handleClose and other
  // non-reactive contexts always read the latest value without a stale closure.
  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  const runtime = useExternalStoreRuntime({
    messages,
    convertMessage: (message) => message,
    setMessages: (nextMessages) => setThreadMessages([...nextMessages]),
    isRunning,
    onNew: async (message) => {
      if (scopeAdoptionPendingRef.current) return;
      clearUnreadBoundary();
      publishUnreadSyncStateRef.current(0);
      const userMessage = appendMessageToThreadMessage(message, useSocketStatusStore.getState().liveAssistance.session);
      const runMessages = [...cancelRunningAssistantMessages(messagesRef.current), userMessage];
      const clientMessageId = getThreadMessageClientMessageId(userMessage) ?? createClientMessageId();
      const assistantMessageId = createThreadMessageId("assistant");
      const runningAssistantMessage = createRunningAssistantMessage(assistantMessageId, clientMessageId);
      setThreadMessages([...runMessages, runningAssistantMessage]);
      const abortController = new AbortController();
      const runEpoch = startRun();
      activeRunsRef.current.set(clientMessageId, {
        assistantCreatedAt: runningAssistantMessage.createdAt ?? new Date(),
        assistantMessageId,
        controller: abortController,
      });
      activeRunClientMessageIdRef.current = clientMessageId;
      let receivedAssistantUpdate = false;
      let markedRunUnread = false;
      let assistantMessage: ThreadMessageLike | null = null;
      let firstVisibleAssistantUpdateAt: number | null = null;
      try {
        for await (const update of runControllerAdapter(
          controller,
          runMessages,
          userMessage.content,
          abortController.signal,
        )) {
          if (runEpoch !== conversationEpochRef.current) break;
          receivedAssistantUpdate = true;
          assistantMessage = runResultToThreadMessage(
            update,
            assistantMessage?.id ?? assistantMessageId,
            getContentText(userMessage.content),
            { type: "running" },
            clientMessageId,
          );
          if (firstVisibleAssistantUpdateAt === null && getContentText(assistantMessage.content).trim()) {
            firstVisibleAssistantUpdateAt = Date.now();
          }
          if (!markedRunUnread && !isWidgetAttentionActive()) {
            markedRunUnread = true;
            markMessageUnreadIfNeeded(assistantMessage.id);
          }
          setThreadMessages((current) => upsertThreadMessage(current, assistantMessage!));
        }
        if (runEpoch === conversationEpochRef.current && assistantMessage && !abortController.signal.aborted) {
          await waitForStreamingIndicatorMinimum(firstVisibleAssistantUpdateAt, abortController.signal);
          assistantMessage = completeAssistantMessage(assistantMessage);
          setThreadMessages((current) => upsertThreadMessage(current, assistantMessage!));
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          receivedAssistantUpdate = true;
          const normalizedError = error instanceof Error ? error : new Error(String(error || "Backend error"));
          const errorMessage: ThreadMessageLike = {
            id: assistantMessage?.id ?? assistantMessageId,
            role: "assistant",
            content: assistantMessage?.content ?? "",
            createdAt: assistantMessage?.createdAt ?? new Date(),
            status: { type: "incomplete", reason: "error", error: normalizedError.message },
            metadata: assistantMessage?.metadata ?? { custom: { clientMessageId } },
          };
          if (!markedRunUnread && !isWidgetAttentionActive()) {
            markedRunUnread = true;
            markMessageUnreadIfNeeded(errorMessage.id);
          }
          setThreadMessages((current) => upsertThreadMessage(current, errorMessage));
        }
      } finally {
        if (runEpoch === conversationEpochRef.current && abortController.signal.aborted && assistantMessage) {
          setThreadMessages((current) =>
            upsertThreadMessage(
              current,
              createStoppedAssistantMessage(
                assistantMessage!,
                assistantMessageId,
                runningAssistantMessage.createdAt ?? new Date(),
              ),
            ),
          );
        } else if (runEpoch === conversationEpochRef.current && !receivedAssistantUpdate) {
          setThreadMessages((current) =>
            abortController.signal.aborted
              ? upsertThreadMessage(
                  current,
                  createCancelledAssistantMessage(assistantMessageId, runningAssistantMessage.createdAt ?? new Date()),
                )
              : current.filter((message) => message.id !== assistantMessageId),
          );
        }
        finishRun(clientMessageId, abortController);
        // Pass the run's assistant row id explicitly: activeRunsRef was just cleared by
        // finishRun and messagesRef can be a render behind, so the drain must not depend
        // on either to locate the row it needs to replace.
        reconcilePendingLocalPersistedTurn(clientMessageId, assistantMessage?.id ?? assistantMessageId);
        if (runEpoch === conversationEpochRef.current) flushPendingLiveTranscript();
      }
    },
    onCancel: async () => {
      const clientMessageId = activeRunClientMessageIdRef.current;
      const activeRun = clientMessageId ? activeRunsRef.current.get(clientMessageId) : null;
      if (!clientMessageId || !activeRun) return;
      activeRun.controller.abort();
      setThreadMessages((current) =>
        current.map((message) =>
          message.role === "assistant" &&
          message.id === activeRun.assistantMessageId &&
          message.status?.type === "running"
            ? createStoppedAssistantMessage(message, activeRun.assistantMessageId, activeRun.assistantCreatedAt)
            : message,
        ),
      );
      activeRunsRef.current.delete(clientMessageId);
      activeRunClientMessageIdRef.current = activeRunsRef.current.keys().next().value ?? null;
      syncRunningState();
    },
    onImport: (importedMessages) => setThreadMessages(cancelRunningAssistantMessages([...importedMessages])),
  });

  useEffect(
    () => () => {
      conversationEpochRef.current += 1;
      activeRunsRef.current.forEach(({ controller }) => controller.abort());
      activeRunsRef.current.clear();
      passiveRunsRef.current.clear();
      activeRunClientMessageIdRef.current = null;
      controller.disconnect();
    },
    [controller],
  );

  useEffect(() => {
    if (rest.chatState === "chat" || !hasKnownConversationIdentity) return;

    let disposed = false;
    void controller
      .connect()
      .then(() => {
        if (disposed) return;
        controller.requestSessionResume();
      })
      .catch(() => {
        // Keep closed/idle widgets quiet; the visible chat path reports window-level errors.
      });
    return () => {
      disposed = true;
    };
  }, [controller, hasKnownConversationIdentity, rest.chatState]);

  useEffect(() => {
    const didEnterChat = prevChatStateRef.current !== "chat" && rest.chatState === "chat";
    const didExitChat = prevChatStateRef.current === "chat" && rest.chatState !== "chat";
    prevChatStateRef.current = rest.chatState;
    if (rest.chatState !== "chat") {
      if (didExitChat) {
        clearConversationState();
        controller.disconnect();
      }
      // Closing the chat is the natural reset for a devtools-forced state.
      forcedWindowContentStateRef.current = null;
      setWindowContentState("ready");
      return;
    }
    const controllerIdentity = controller.getIdentity();
    const hasControllerConversationIdentity = Boolean(
      controllerIdentity.guestId ||
        controllerIdentity.threadId ||
        controllerIdentity.memoryId ||
        controllerIdentity.userToken,
    );
    const shouldStartFreshAnonymousSession =
      didEnterChat &&
      !hasConfiguredConversationIdentity &&
      !hasControllerConversationIdentity &&
      !conversationStartedAtRef.current &&
      messagesRef.current.length === 0;
    const connectSequence = ++connectSequenceRef.current;
    const shouldBlockWindow = messagesRef.current.length === 0;

    if (shouldStartFreshAnonymousSession) {
      clearConversationState();
      suppressNextResumeRef.current = true;
      const freshIdentity = createFreshChatSessionIdentity();
      controller.resetSession({ guestId: freshIdentity.guestId ?? undefined, preserveConfiguredGuest: false });
    }
    if (shouldBlockWindow) {
      applyWindowContentState("loading");
    }
    // Record when the session first enters chat state for duration tracking.
    // Only set once — preserve the original start time across reopen/reconnect.
    if (!conversationStartedAtRef.current) {
      conversationStartedAtRef.current = Date.now();
    }
    void controller
      .connect()
      .then(() => {
        if (connectSequence !== connectSequenceRef.current) return;
        applyWindowContentState("ready");
        void controller.publishWidgetShellSync({
          version: 1,
          eventId: createSyncEventId("shell"),
          sourceId: rest.shellSyncSourceId,
          status: chatShellStatusForState(rest.chatState, rest.isMinimized),
          layout,
          updatedAt: Date.now(),
        });
        if (!shouldStartFreshAnonymousSession) controller.requestSessionResume();
      })
      .catch(() => {
        if (connectSequence !== connectSequenceRef.current) return;
        applyWindowContentState(shouldBlockWindow ? "error" : "ready");
      });
  }, [controller, hasConfiguredConversationIdentity, rest.chatState]);

  // Notify the Genesys agent when the student navigates away or closes the tab
  // while a live session is active. Uses `pagehide` (reliable on mobile) with
  // `beforeunload` as a fallback for older desktop browsers. A guard flag
  // prevents duplicate emits when both events fire for the same navigation.
  useEffect(() => {
    let didEnd = false;
    const endLiveOnUnload = () => {
      if (didEnd) return;
      const liveSession = useSocketStatusStore.getState().liveAssistance.session;
      const activeStates = new Set(["connecting", "waiting_for_specialist", "live"]);
      if (liveSession?.state && activeStates.has(liveSession.state)) {
        didEnd = true;
        controller.endLiveSession("student_navigated_away").catch(() => {});
      }
    };
    window.addEventListener("pagehide", endLiveOnUnload);
    window.addEventListener("beforeunload", endLiveOnUnload);
    return () => {
      window.removeEventListener("pagehide", endLiveOnUnload);
      window.removeEventListener("beforeunload", endLiveOnUnload);
    };
  }, [controller]);

  // When the user reopens a minimized panel (isMinimized: true→false), resume
  // the session so the backend knows the widget is visible again. Guard with
  // prevIsMinimizedRef so this only fires on actual true→false transitions —
  // not on initial mount or on unrelated re-renders.
  useEffect(() => {
    const wasMinimized = prevIsMinimizedRef.current;
    prevIsMinimizedRef.current = rest.isMinimized;
    if (!wasMinimized || rest.isMinimized) return;
    // Only resume if we're in chat state and not mid-run.
    if (rest.chatState !== "chat" || isRunningRef.current) return;
    controller.requestSessionResume();
  }, [controller, rest.isMinimized, rest.chatState]);

  const resetConversationTelemetryState = () => {
    conversationStartedRef.current = false;
    conversationStartedAtRef.current = null;
    threadIdRef.current = null;
    setThreadId(null);
  };

  const clearConversationState = () => {
    conversationEpochRef.current += 1;
    activeRunsRef.current.forEach(({ controller }) => controller.abort());
    activeRunsRef.current.clear();
    passiveRunsRef.current.clear();
    activeRunClientMessageIdRef.current = null;
    setThreadMessages([]);
    setAsyncSuggestions(new Map());
    setIsRunning(false);
    setIsPassiveTurnRunning(false);
    isRunningRef.current = false;
    hydratedThreadRef.current = null;
    pendingHandoffMarkerRef.current = null;
    pendingLiveMessagesRef.current = [];
    playedSentMessageIdsRef.current.clear();
    playedReceivedMessageIdsRef.current.clear();
    localClientMessageIdsRef.current.clear();
    appliedPersistedTurnTraceIdsRef.current.clear();
    pendingLocalPersistedTurnsRef.current.clear();
    discardPersistedTurnsRef.current = true;
    clearUnreadBoundary();
    setSyncedUnreadCount(0);
    resetConversationTelemetryState();
  };

  useEffect(() => {
    if (!rest.enableDevtools || typeof window === "undefined") return;

    const seedDateMessageCases = (cases: DateMessageTestCase[]) => {
      if (chatStateRef.current !== "chat") {
        onButtonClickRef.current();
      }
      suppressNextResumeRef.current = true;
      hydratedThreadRef.current = "devtools-date-messages";
      clearConversationState();
      setThreadMessages(createDateTestMessages(cases));
    };

    // Pins the window shell to a state for QA. Opening the chat kicks off a
    // connect whose loading/resolution steps would otherwise overwrite the
    // forced value, so record it in a ref the connect effect honours (VIAF-455).
    const forceWindowContentState = (state: WindowContentState) => {
      forcedWindowContentStateRef.current = state === "ready" ? null : state;
      if (chatStateRef.current !== "chat") {
        onButtonClickRef.current();
      }
      setWindowContentState(state);
    };

    const previousDevtools = window.__PHOEBE_DEVTOOLS__;
    const installedDevtools = {
      ...previousDevtools,
      seedDateMessages: () => seedDateMessageCases(["today", "yesterday", "current-year", "prior-year"]),
      seedTodayMessage: () => seedDateMessageCases(["today"]),
      seedYesterdayMessage: () => seedDateMessageCases(["yesterday"]),
      seedCurrentYearDateMessage: () => seedDateMessageCases(["current-year"]),
      seedPriorYearDateMessage: () => seedDateMessageCases(["prior-year"]),
      clearDateMessages: () => {
        suppressNextResumeRef.current = true;
        hydratedThreadRef.current = "devtools-date-messages";
        clearConversationState();
      },
      resetLauncherNudgeState: resetLauncherNudgeResolution,
      resetLegalNoticeReminderState: () => syncLegalNoticeReminderState(EMPTY_LEGAL_NOTICE_REMINDER_STATE),
      restoreLegalNoticeReminderInterval: () => syncLegalNoticeReminderState(
        removeLegalNoticeReminder(
          ensureLegalNoticeThreadStarted(
            EMPTY_LEGAL_NOTICE_REMINDER_STATE,
            Date.now(),
            LEGAL_NOTICE_REMINDER_INTERVAL_MS,
          ),
        ),
      ),
      setLegalNoticeReminderIntervalMs: (intervalMs: number) => syncLegalNoticeReminderState(
        removeLegalNoticeReminder(
          ensureLegalNoticeThreadStarted(EMPTY_LEGAL_NOTICE_REMINDER_STATE, Date.now(), intervalMs),
        ),
      ),
      triggerWindowError: () => forceWindowContentState("error"),
      triggerWindowLoader: () => forceWindowContentState("loading"),
      clearWindowState: () => forceWindowContentState("ready"),
    } satisfies PhoebeDevtools;
    window.__PHOEBE_DEVTOOLS__ = installedDevtools;

    return () => {
      if (window.__PHOEBE_DEVTOOLS__ !== installedDevtools) return;
      window.__PHOEBE_DEVTOOLS__ = previousDevtools;
    };
  // Devtools methods read current values from refs, so installing once per enabled mount is enough.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest.enableDevtools]);

  const retireCurrentConversationScope = () => {
    const currentIdentity = controller.getIdentity();
    const retiredIdentity = trustedUnreadIdentityRef.current ?? (
      currentIdentity.threadId && currentIdentity.memoryId
        ? {
            guestId: currentIdentity.guestId ?? undefined,
            threadId: currentIdentity.threadId,
            memoryId: currentIdentity.memoryId,
          }
        : null
    );
    retiredResumeIdentityRef.current = retiredIdentity;
    trustedUnreadIdentityRef.current = null;
    return retiredIdentity;
  };

  // The sandbox host page may acquire a *new* server-owned widget credential
  // after its own Ping login. The browser receives only a completion signal;
  // Socket.IO must reconnect to bind the HttpOnly cookie before sending again.
  useEffect(() => {
    let active = true;
    let resumeGeneration = 0;
    const resumeWithCurrentCookie = () => {
      const generation = ++resumeGeneration;
      connectSequenceRef.current += 1;
      replaceMessagesOnNextResumeRef.current = true;
      setScopeAdoptionPending(true);
      controller.disconnect();
      void controller.connect().then(() => {
        if (active && generation === resumeGeneration) controller.requestSessionResume();
      }).catch(() => {
        if (active && generation === resumeGeneration) setScopeAdoptionPending(false);
      });
    };
    const fence = () => {
      resumeGeneration += 1;
      connectSequenceRef.current += 1;
      retireCurrentConversationScope();
      setScopeAdoptionPending(true);
      clearConversationState();
      controller.disconnect();
    };
    const guest = () => {
      fence();
      const generation = ++resumeGeneration;
      replaceMessagesOnNextResumeRef.current = true;
      try {
        getChatShellSyncStorage()?.removeItem(CHAT_SESSION_IDENTITY_STORAGE_KEY);
      } catch {
        // Server-owned guest rotation must proceed even if browser storage is unavailable.
      }
      controller.resetSession({ preserveConfiguredGuest: false, resetGuestOnNextBootstrap: true });
      void controller.connect().then(() => {
        if (active && generation === resumeGeneration) controller.requestSessionResume();
      }).catch(() => {
        // Keep the old authenticated room fenced until guest bootstrap succeeds.
      });
    };
    const unfence = () => {
      retiredResumeIdentityRef.current = null;
      resumeWithCurrentCookie();
    };
    window.addEventListener("phoebe:widget-auth-activated", unfence);
    window.addEventListener("phoebe:widget-auth-fence", fence);
    window.addEventListener("phoebe:widget-auth-guest", guest);
    window.addEventListener("phoebe:widget-auth-unfence", unfence);
    return () => {
      active = false;
      resumeGeneration += 1;
      connectSequenceRef.current += 1;
      controller.disconnect();
      window.removeEventListener("phoebe:widget-auth-activated", unfence);
      window.removeEventListener("phoebe:widget-auth-fence", fence);
      window.removeEventListener("phoebe:widget-auth-guest", guest);
      window.removeEventListener("phoebe:widget-auth-unfence", unfence);
    };
  }, [controller]);

  const resetConversationForNewChat = async ({ publish, track }: { publish: boolean; track: boolean }): Promise<boolean> => {
    if (publish) {
      const syncState = createConversationSyncState("new_chat", conversationSyncSourceIdRef.current);
      const previousRetiredIdentity = retiredResumeIdentityRef.current;
      const retiredIdentity = retireCurrentConversationScope();
      const rotated = await controller.startNewChat(syncState).catch(() => null);
      if (!rotated) {
        retiredResumeIdentityRef.current = previousRetiredIdentity;
        trustedUnreadIdentityRef.current = retiredIdentity;
        return false;
      }
      if (track) trackConversationEnded();
      clearConversationState();
      discardPersistedTurnsRef.current = false;
      writeChatConversationSyncState(getChatShellSyncStorage(), syncState);
      postBroadcastMessage(CHAT_CONVERSATION_SYNC_CHANNEL, syncState);
    } else {
      if (track) trackConversationEnded();
      retireCurrentConversationScope();
      replaceMessagesOnNextResumeRef.current = true;
      setScopeAdoptionPending(true);
      clearConversationState();
      revalidatePersistedUnreadSyncStateRef.current();
      controller.requestSessionResume();
    }
    syncLegalNoticeReminderState({
      ...EMPTY_LEGAL_NOTICE_REMINDER_STATE,
      intervalMs: readLegalNoticeReminderState().intervalMs,
    });
    return true;
  };

  const trackConversationEnded = () => {
    if (!conversationStartedRef.current) return;
    track("Live Chat Conversation Ended", {
      conversation_id: threadIdRef.current ?? undefined,
      conversation_duration: getConversationDuration(),
    });
  };

  const handleNewChat = () => {
    void resetConversationForNewChat({ publish: true, track: true });
  };

  useEffect(() => {
    applyConversationSyncStateRef.current = (syncedState: ChatConversationSyncState | null) => {
      if (!syncedState) return;
      if (syncedState.sourceId === conversationSyncSourceIdRef.current) return;
      if (syncedState.updatedAt <= lastAppliedConversationSyncAtRef.current) return;

      lastAppliedConversationSyncAtRef.current = syncedState.updatedAt;
      if (syncedState.action === "new_chat") {
        void resetConversationForNewChat({ publish: false, track: false });
      }
    };
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const applyConversationSyncState = (syncedState: ChatConversationSyncState | null) => {
      applyConversationSyncStateRef.current(syncedState);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== CHAT_CONVERSATION_SYNC_STORAGE_KEY) return;
      applyConversationSyncState(parseChatConversationSyncState(event.newValue));
    };

    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHAT_CONVERSATION_SYNC_CHANNEL);
    if (channel) {
      channel.onmessage = (event) => {
        applyConversationSyncState(parseChatConversationSyncState(JSON.stringify(event.data)));
      };
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      channel?.close();
    };
  }, []);

  useEffect(() => {
    applyMessageSyncStateRef.current = applyRemoteMessageSync;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (rest.chatState === "chat") {
      const storage = getChatShellSyncStorage();
      applyMessageSyncStateRef.current(parseChatMessageSyncState(storage?.getItem(CHAT_MESSAGE_SYNC_STORAGE_KEY) ?? null));
    }
  }, [rest.chatState]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const applyMessageSyncState = (syncedState: ChatMessageSyncState | null) => {
      applyMessageSyncStateRef.current(syncedState);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== CHAT_MESSAGE_SYNC_STORAGE_KEY) return;
      applyMessageSyncState(parseChatMessageSyncState(event.newValue));
    };

    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHAT_MESSAGE_SYNC_CHANNEL);
    if (channel) {
      channel.onmessage = (event) => {
        applyMessageSyncState(parseChatMessageSyncState(JSON.stringify(event.data)));
      };
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      channel?.close();
    };
  }, []);

  useEffect(() => {
    applyPresenceSyncStateRef.current = (syncedState: ChatPresenceSyncState | null) => {
      if (!syncedState) return;
      if (syncedState.sourceId === presenceSyncSourceIdRef.current) return;
      if (syncedState.updatedAt <= lastAppliedPresenceSyncAtRef.current) return;
      lastAppliedPresenceSyncAtRef.current = syncedState.updatedAt;
    };

    applyUnreadSyncStateRef.current = (syncedState: ChatUnreadSyncState | null) => {
      if (!syncedState) return;
      if (syncedState.sourceId === unreadSyncSourceIdRef.current) return;
      if (syncedState.updatedAt <= lastAppliedUnreadSyncAtRef.current) return;
      const trustedIdentity = trustedUnreadIdentityRef.current;
      if (!trustedIdentity || !unreadSyncMatchesConversationIdentity(syncedState, trustedIdentity)) return;
      lastAppliedUnreadSyncAtRef.current = syncedState.updatedAt;
      if (syncedState.unreadCount > 0 && isWidgetAttentionActive()) {
        publishUnreadSyncStateRef.current(0);
        return;
      }
      setSyncedUnreadCount(syncedState.unreadCount);
    };

    revalidatePersistedUnreadSyncStateRef.current = () => {
      const storage = getChatShellSyncStorage();
      const syncedState = parseChatUnreadSyncState(storage?.getItem(CHAT_UNREAD_SYNC_STORAGE_KEY) ?? null);
      const trustedIdentity = trustedUnreadIdentityRef.current;
      if (!syncedState || !trustedIdentity || !unreadSyncMatchesConversationIdentity(syncedState, trustedIdentity)) {
        lastAppliedUnreadSyncAtRef.current = 0;
        setSyncedUnreadCount(0);
        return;
      }
      lastAppliedUnreadSyncAtRef.current = syncedState.updatedAt;
      if (syncedState.unreadCount > 0 && isWidgetAttentionActive()) {
        publishUnreadSyncStateRef.current(0);
        return;
      }
      setSyncedUnreadCount(syncedState.unreadCount);
    };
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const applyPresenceSyncState = (syncedState: ChatPresenceSyncState | null) => {
      applyPresenceSyncStateRef.current(syncedState);
    };
    const applyUnreadSyncState = (syncedState: ChatUnreadSyncState | null) => {
      applyUnreadSyncStateRef.current(syncedState);
    };

    const storage = getChatShellSyncStorage();
    applyPresenceSyncState(parseChatPresenceSyncState(storage?.getItem(CHAT_PRESENCE_SYNC_STORAGE_KEY) ?? null));

    const handleStorage = (event: StorageEvent) => {
      if (event.key === CHAT_PRESENCE_SYNC_STORAGE_KEY) {
        applyPresenceSyncState(parseChatPresenceSyncState(event.newValue));
      } else if (event.key === CHAT_UNREAD_SYNC_STORAGE_KEY) {
        applyUnreadSyncState(parseChatUnreadSyncState(event.newValue));
      }
    };

    const presenceChannel =
      typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHAT_PRESENCE_SYNC_CHANNEL);
    if (presenceChannel) {
      presenceChannel.onmessage = (event) => {
        applyPresenceSyncState(parseChatPresenceSyncState(JSON.stringify(event.data)));
      };
    }

    const unreadChannel =
      typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHAT_UNREAD_SYNC_CHANNEL);
    if (unreadChannel) {
      unreadChannel.onmessage = (event) => {
        applyUnreadSyncState(parseChatUnreadSyncState(JSON.stringify(event.data)));
      };
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      presenceChannel?.close();
      unreadChannel?.close();
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const markFocused = () => {
      documentFocusedRef.current = true;
      publishPresenceSyncStateRef.current("tab_focused", true);
      if (isWidgetAttentionActive()) publishUnreadSyncStateRef.current(0);
    };
    const markBlurred = () => {
      documentFocusedRef.current = false;
      publishPresenceSyncStateRef.current("tab_blurred", false);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        markBlurred();
      } else {
        markFocused();
      }
    };
    const markOffline = () => {
      publishPresenceSyncStateRef.current("user_offline", false);
    };

    publishPresenceSyncStateRef.current("user_online", !document.hidden);
    window.addEventListener("focus", markFocused);
    window.addEventListener("blur", markBlurred);
    window.addEventListener("pagehide", markOffline);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", markFocused);
      window.removeEventListener("blur", markBlurred);
      window.removeEventListener("pagehide", markOffline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (isWidgetAttentionActive()) publishUnreadSyncStateRef.current(0);
  }, [rest.chatState, rest.isMinimized]);

  const handleScheduleConsultation = () => {
    runtime.thread.append("Schedule a consultation");
  };

  const handleRuntimeMinimize = () => {
    isMinimizedRef.current = true;
    rest.onMinimize();
  };

  const handleRuntimeReopen = () => {
    isMinimizedRef.current = false;
    rest.onReopen();
  };

  const handleClose = async () => {
    // If a live session is in any non-terminal state, notify the agent before
    // disconnecting. Wrapped in try/catch so a socket timeout doesn't prevent cleanup.
    const liveSession = useSocketStatusStore.getState().liveAssistance.session;
    const activeStates = new Set(["connecting", "waiting_for_specialist", "live"]);
    if (liveSession?.state && activeStates.has(liveSession.state)) {
      try {
        await controller.endLiveSession("student_closed_widget");
      } catch {
        // Best-effort: proceed with cleanup even if the emit fails
      }
    }
    trackConversationEnded();
    clearConversationState();
        await controller.publishWidgetShellSync({
          version: 1,
          eventId: createSyncEventId("shell"),
          sourceId: rest.shellSyncSourceId,
          status: "closed",
          layout,
      updatedAt: Date.now(),
    });
    controller.disconnect();
    rest.onClose();
  };

  const handleSafetyExit = async () => {
    const resetLocalSafetySession = () => {
      pendingSafetyExitResetRef.current = true;
      trustedUnreadIdentityRef.current = null;
      clearConversationState();
      const nextIdentity = recordSafetyExitSessionReset();
      controller.resetSession({
        guestId: nextIdentity.guestId ?? undefined,
        preserveConfiguredGuest: false,
        resetGuestOnNextBootstrap: true,
      });
    };
    const liveSession = useSocketStatusStore.getState().liveAssistance.session;
    const activeStates = new Set(["connecting", "waiting_for_specialist", "live"]);
    if (liveSession?.state && activeStates.has(liveSession.state)) {
      try {
        await controller.endLiveSession("student_safety_exit");
      } catch {
        // Best-effort: still clear sensitive chat state even if the live end emit fails.
      }
    }

    trackConversationEnded();
    retireCurrentConversationScope();
    clearConversationState();
    suppressNextResumeRef.current = true;
    await controller.publishWidgetShellSync({
      version: 1,
      eventId: createSyncEventId("shell"),
      sourceId: rest.shellSyncSourceId,
      status: "closed",
      layout,
      updatedAt: Date.now(),
    }).catch(() => false);
    const rotated = await resetConversationForNewChat({ publish: true, track: false });
    if (!rotated) {
      resetLocalSafetySession();
      rest.onClose();
      return;
    }
    resetLocalSafetySession();
    rest.onClose();
  };

  return (
    <ComposerSubmissionContext.Provider value={composerSubmission}>
      <ModalShell
        runtime={runtime}
        backendUrl={backendUrl}
        canCancelActiveTurn={!isPassiveTurnRunning}
        layout={layout}
        windowContentState={windowContentState}
        {...rest}
        unreadCount={unreadCount}
        unreadBoundaryMessageId={unreadBoundaryMessageId}
        onClose={handleClose}
        onMinimize={handleRuntimeMinimize}
        onReopen={handleRuntimeReopen}
        onNewChat={handleNewChat}
        onEndLiveSession={() => void controller.endLiveSession()}
        onSafetyExit={handleSafetyExit}
        onAssistantFeedback={onAssistantFeedback}
        onScheduleConsultation={handleScheduleConsultation}
        asyncSuggestions={asyncSuggestions}
      />
    </ComposerSubmissionContext.Provider>
  );
}

function mergeResumedThreadMessages(
  current: ThreadMessageLike[],
  resumedMessages: ThreadMessageLike[],
  marker: ThreadMessageLike | null,
  hasActiveRun: (clientMessageId: string) => boolean = () => false,
): ThreadMessageLike[] {
  const resumed = marker && !resumedMessages.some((message) => message.id === marker.id)
    ? [...resumedMessages, marker]
    : resumedMessages;
  const resumedIds = new Set(resumed.map((message) => message.id).filter(Boolean));
  const resumedClientMessageIds = new Set(
    resumed.map((message) => getThreadMessageClientMessageId(message)).filter(Boolean),
  );
  const resumedUserTexts = new Set(
    resumed
      .filter((message) => message.role === "user")
      .map((message) => getContentText(message.content).trim())
      .filter(Boolean),
  );
  return [
    ...resumed,
    ...current.filter((message) => {
      if (message.id && resumedIds.has(message.id)) return false;
      // A resumed turn is the canonical copy of the same exchange: drop the local rows it
      // supersedes (matched by clientMessageId) so a broken live row cannot linger beside
      // the saved answer. Rows still streaming keep rendering until their run settles.
      const clientMessageId = getThreadMessageClientMessageId(message);
      if (clientMessageId && resumedClientMessageIds.has(clientMessageId) && !hasActiveRun(clientMessageId)) {
        return false;
      }
      const custom = message.metadata?.custom as Record<string, unknown> | undefined;
      if (
        message.role === "user" &&
        custom?.syncedFrom === "widget.message.sync" &&
        resumedUserTexts.has(getContentText(message.content).trim())
      ) {
        return false;
      }
      return true;
    }),
  ];
}

function appendMessageToThreadMessage(
  message: AppendMessage,
  liveSession: LiveAssistanceSessionPayload | null,
): ThreadMessageLike {
  const clientMessageId = createClientMessageId();
  const liveSessionId = liveSession?.live_session_id;
  const isLiveStudentMessage = Boolean(
    liveSessionId && ["waiting_for_specialist", "live"].includes(liveSession?.state ?? ""),
  );
  return {
    id: `user-${clientMessageId}`,
    role: "user",
    content: message.content,
    createdAt: new Date(),
    metadata: {
      custom: {
        clientMessageId,
        ...(isLiveStudentMessage
          ? {
              liveAssistance: {
                kind: "student_message",
                clientMessageId,
                liveSessionId,
              },
            }
          : {}),
      },
    },
  };
}

function createDateTestMessages(cases: readonly DateMessageTestCase[]): ThreadMessageLike[] {
  return cases.map((testCase) => {
    const createdAt = dateForMessageTestCase(testCase);
    return {
      id: `devtools-date-${testCase}-${createdAt.getTime()}`,
      role: "assistant",
      content: dateMessageTestContent(testCase),
      createdAt,
      status: { type: "complete", reason: "unknown" },
      metadata: {
        custom: {
          devtools: {
            testCase,
            kind: "date-separator",
          },
        },
      },
    };
  });
}

function dateForMessageTestCase(testCase: DateMessageTestCase): Date {
  const now = new Date();
  switch (testCase) {
    case "today":
      return atLocalNoon(now.getFullYear(), now.getMonth(), now.getDate());
    case "yesterday":
      return atLocalNoon(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    case "current-year":
      return currentYearOlderDate(now);
    case "prior-year":
      return atLocalNoon(Math.min(2024, now.getFullYear() - 1), 11, 24);
  }
}

function currentYearOlderDate(now: Date): Date {
  const julyFirst = atLocalNoon(now.getFullYear(), 6, 1);
  if (julyFirst.getTime() < atLocalNoon(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime()) {
    return julyFirst;
  }

  const twoDaysAgo = atLocalNoon(now.getFullYear(), now.getMonth(), now.getDate() - 2);
  if (twoDaysAgo.getFullYear() === now.getFullYear()) return twoDaysAgo;
  return atLocalNoon(now.getFullYear(), 0, 1);
}

function atLocalNoon(year: number, month: number, day: number): Date {
  return new Date(year, month, day, 12, 0, 0, 0);
}

function dateMessageTestContent(testCase: DateMessageTestCase): string {
  switch (testCase) {
    case "today":
      return "Date utility test message: Today.";
    case "yesterday":
      return "Date utility test message: Yesterday.";
    case "current-year":
      return "Date utility test message: earlier this year.";
    case "prior-year":
      return "Date utility test message: prior year.";
  }
}

function getThreadMessageClientMessageId(message: ThreadMessageLike): string | null {
  const custom = message.metadata?.custom as
    | {
        clientMessageId?: unknown;
        liveAssistance?: { clientMessageId?: unknown };
      }
    | undefined;
  const clientMessageId = custom?.clientMessageId ?? custom?.liveAssistance?.clientMessageId;
  return typeof clientMessageId === "string" && clientMessageId.trim() ? clientMessageId : null;
}

export function hasThreadMessageClientId(
  messages: readonly ThreadMessageLike[],
  clientMessageId: string,
): boolean {
  return messages.some(
    (message) => message.role === "user" && getThreadMessageClientMessageId(message) === clientMessageId,
  );
}

export function filterStoppedAssistantMessages(
  messages: readonly ThreadMessageLike[],
  stoppedClientMessageIds: ReadonlySet<string>,
): ThreadMessageLike[] {
  return messages.filter(
    (message) =>
      message.role !== "assistant" ||
      !stoppedClientMessageIds.has(getThreadMessageClientMessageId(message) ?? ""),
  );
}

async function* runControllerAdapter(
  controller: ReturnType<typeof createSocketChatRuntimeController>,
  messages: readonly ThreadMessageLike[],
  content: ThreadMessageLike["content"],
  abortSignal: AbortSignal,
): AsyncGenerator<ChatModelRunResult, void> {
  const userText = getContentText(content);
  const context = {};
  const runOptions = {
    messages: messages.map(threadLikeToRunMessage),
    abortSignal,
    context,
    config: context,
    runConfig: {},
    unstable_getMessage: () =>
      threadLikeToRunMessage({ id: createThreadMessageId("user"), role: "user", content: userText }),
  } as ChatModelRunOptions & { config: typeof context };
  const result = controller.adapter.run(runOptions);

  if (isAsyncGenerator(result)) {
    for await (const update of result) {
      yield update;
    }
    return;
  }

  const update = await result;
  if (update) {
    yield update;
  }
}

function threadLikeToRunMessage(message: ThreadMessageLike): ThreadMessage {
  const base = {
    id: message.id ?? createThreadMessageId("message"),
    createdAt: message.createdAt ?? new Date(),
  };
  const content = textContent(message.content);
  if (message.role === "assistant") {
    return {
      ...base,
      role: "assistant",
      content,
      status: message.status ?? { type: "complete", reason: "unknown" },
      metadata: {
        unstable_state: message.metadata?.unstable_state ?? null,
        unstable_annotations: message.metadata?.unstable_annotations ?? [],
        unstable_data: message.metadata?.unstable_data ?? [],
        steps: message.metadata?.steps ?? [],
        custom: message.metadata?.custom ?? {},
        ...(message.metadata?.timing ? { timing: message.metadata.timing } : {}),
        ...(message.metadata?.submittedFeedback ? { submittedFeedback: message.metadata.submittedFeedback } : {}),
      },
    };
  }
  if (message.role === "system") {
    const systemContent = textContent(message.content);
    return {
      ...base,
      role: "system",
      content: systemContent,
      metadata: { custom: message.metadata?.custom ?? {} },
    };
  }
  return {
    ...base,
    role: "user",
    content,
    attachments: [],
    metadata: { custom: message.metadata?.custom ?? {} },
  };
}

function runResultToThreadMessage(
  update: ChatModelRunResult,
  existingId?: string,
  triggeringUserText?: string,
  status: ThreadMessageLike["status"] = { type: "complete", reason: "unknown" },
  clientMessageId?: string,
): ThreadMessageLike {
  const metadata = withAssistantTurnMetadata(update.metadata, triggeringUserText);
  // Stamp the originating clientMessageId on every streamed update — not just the final —
  // so persisted-turn reconciliation can locate the row even when the stream breaks
  // before the final payload's metadata is applied.
  const custom = metadata?.custom as { clientMessageId?: unknown } | undefined;
  const metadataWithClientId =
    clientMessageId && !custom?.clientMessageId
      ? { ...metadata, custom: { ...(custom ?? {}), clientMessageId } }
      : metadata;
  return {
    id: existingId ?? createThreadMessageId("assistant"),
    role: "assistant",
    content: update.content ?? "",
    createdAt: new Date(),
    status,
    metadata: metadataWithClientId,
  };
}

function completeAssistantMessage(message: ThreadMessageLike): ThreadMessageLike {
  if (message.role !== "assistant") return message;
  return {
    ...message,
    status: { type: "complete", reason: "unknown" },
  };
}

function waitForStreamingIndicatorMinimum(startedAt: number | null, abortSignal: AbortSignal): Promise<void> {
  if (startedAt === null || abortSignal.aborted) return Promise.resolve();

  const remainingMs = STREAMING_INDICATOR_MIN_VISIBLE_MS - (Date.now() - startedAt);
  if (remainingMs <= 0) return Promise.resolve();

  return new Promise((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const finish = () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      abortSignal.removeEventListener("abort", finish);
      resolve();
    };

    timeoutId = setTimeout(finish, remainingMs);
    abortSignal.addEventListener("abort", finish, { once: true });
  });
}

function createRunningAssistantMessage(id: string, clientMessageId?: string): ThreadMessageLike {
  return {
    id,
    role: "assistant",
    content: "",
    createdAt: new Date(),
    status: { type: "running" },
    metadata: { custom: clientMessageId ? { clientMessageId } : {} },
  };
}

function createCancelledAssistantMessage(id: string, createdAt = new Date()): ThreadMessageLike {
  return {
    id,
    role: "assistant",
    content: "",
    createdAt,
    status: { type: "incomplete", reason: "cancelled" },
    metadata: { custom: {} },
  };
}

function createStoppedAssistantMessage(
  message: ThreadMessageLike,
  fallbackId: string,
  fallbackCreatedAt = new Date(),
): ThreadMessageLike {
  const id = message.id ?? fallbackId;
  const createdAt = message.createdAt ?? fallbackCreatedAt;
  if (!hasVisibleMessageContent(message.content)) {
    return createCancelledAssistantMessage(id, createdAt);
  }
  return {
    ...message,
    id,
    createdAt,
    status: { type: "incomplete", reason: "cancelled" },
    metadata: message.metadata ?? { custom: {} },
  };
}

function cancelRunningAssistantMessages(messages: ThreadMessageLike[]): ThreadMessageLike[] {
  let didCancel = false;
  const nextMessages = messages.map((message) => {
    if (message.role !== "assistant" || message.status?.type !== "running") return message;

    didCancel = true;
    return createStoppedAssistantMessage(
      message,
      message.id ?? createThreadMessageId("assistant"),
      message.createdAt ?? new Date(),
    );
  });

  return didCancel ? nextMessages : messages;
}

function upsertThreadMessage(messages: ThreadMessageLike[], next: ThreadMessageLike): ThreadMessageLike[] {
  if (!next.id) return [...messages, next];
  const index = messages.findIndex((message) => message.id === next.id);
  if (index === -1) return [...messages, next];
  return messages.map((message, currentIndex) => (currentIndex === index ? next : message));
}

function appendThreadMessageIfNew(messages: ThreadMessageLike[], next: ThreadMessageLike): ThreadMessageLike[] {
  if (next.id && messages.some((message) => message.id === next.id)) return messages;
  return [...messages, next];
}

function liveMessageToThreadMessage(payload: SupervisorLiveMessagePayload): ThreadMessageLike | null {
  const text = sanitizeLiveAssistanceMarkdownText(payload.text);
  if (!text) return null;
  return {
    id: payload.event_id ?? payload.provider_message_id ?? createThreadMessageId("live-message"),
    role: "assistant",
    content: text,
    createdAt: payload.occurred_at ? new Date(payload.occurred_at) : new Date(),
    metadata: {
      custom: {
        liveAssistance: {
          kind: "agent_message",
          agentName: payload.agent?.display_name ?? "Support Specialist",
          liveSessionId: payload.live_session_id,
        },
      },
    },
  };
}

function liveHandoffToThreadMessage(payload: Partial<SupervisorHandoffPayload>): ThreadMessageLike | null {
  const session = payload.live_assistance;
  const liveSessionId = session?.live_session_id;
  if (!liveSessionId || session?.state === "ended" || session?.state === "failed") return null;
  if (!session.agent?.display_name && session.state !== "live") return null;
  const agentName = session.agent?.display_name ?? "Support Specialist";
  
  // TODO: Display conversation summary from handoff_signal.conversation_summary
  // The supervisor agent generates an AI-powered summary of the conversation history
  // that could be shown to the student or agent. Current implementation only sends
  // it to the agent workspace (not visible to student).
  // Placeholder for future enhancement: Extract summary from payload and display it here.
  
  return {
    id: `live-handoff-${liveSessionId}`,
    role: "assistant",
    content: `You're now connected to a live agent ${agentName}`,
    createdAt: new Date(),
    metadata: {
      custom: {
        liveAssistance: {
          kind: "handoff_marker",
          agentName,
          liveSessionId,
        },
      },
    },
  };
}

function liveHandoffMarkerFromLiveMessage(payload: SupervisorLiveMessagePayload): ThreadMessageLike | null {
  const liveSessionId = payload.live_session_id;
  if (!liveSessionId) return null;
  return liveHandoffToThreadMessage({
    live_assistance: {
      live_session_id: liveSessionId,
      provider: payload.provider,
      state: payload.state ?? "live",
      agent: payload.agent,
    },
  });
}

// Genesys boilerplate system messages that should not be shown to students
const GENESYS_SYSTEM_MESSAGE_FILTERS: Array<string | RegExp> = [
  "Connecting to a representative...",
  /^NOTE: this chat is currently connected to/i,
  /^this chat is currently connected to/i,
];

function shouldFilterGenesysSystemMessage(text: string): boolean {
  return GENESYS_SYSTEM_MESSAGE_FILTERS.some((filter) => {
    if (typeof filter === "string") {
      return text === filter;
    }
    return filter.test(text);
  });
}

function normalizeGenesysQueueStatusText(text: string): string | null {
  const aheadMatch = text.match(/\b(\d+)\b[\s\S]{0,80}\bahead of you\b/i);
  if (aheadMatch?.[1]) {
    return `There are currently ${Number(aheadMatch[1])} chat(s) ahead of you`;
  }

  const positionMatch = text.match(/\b(?:number|position)\s+(\d+)\b/i);
  if (positionMatch?.[1]) {
    return `There are currently ${Math.max(Number(positionMatch[1]) - 1, 0)} chat(s) ahead of you`;
  }

  return null;
}

export function liveSystemMessageToThreadMessage(payload: SupervisorLiveMessagePayload): ThreadMessageLike | null {
  const rawText = payload.text?.trim();
  if (!rawText) return null;

  // Filter out Genesys boilerplate system messages
  if (shouldFilterGenesysSystemMessage(rawText)) {
    return null;
  }

  const queueStatusText = normalizeGenesysQueueStatusText(rawText);
  const content = queueStatusText ?? sanitizeLiveAssistanceMarkdownText(rawText);
  const kind = queueStatusText ? "queue_status" : "system_message";

  return {
    id: payload.event_id ?? payload.provider_message_id ?? createThreadMessageId("live-system"),
    role: "assistant",
    content,
    createdAt: payload.occurred_at ? new Date(payload.occurred_at) : new Date(),
    metadata: {
      custom: {
        liveAssistance: {
          kind,
          liveSessionId: payload.live_session_id,
        },
      },
    },
  };
}

export function liveEndedToThreadMessage(payload: SupervisorLiveEndedPayload): ThreadMessageLike {
  const timestamp = payload.occurred_at ? new Date(payload.occurred_at) : new Date();
  
  // Guard against Invalid Date from malformed occurred_at payload
  const validTimestamp = !isNaN(timestamp.getTime()) ? timestamp : new Date();
  
  const content = `Live chat ended\nYou're now connected to Phoebe - AI Assistant`;
  
  return {
    id: `live-ended-${payload.live_session_id ?? createThreadMessageId("live")}`,
    role: "assistant",
    content,
    createdAt: validTimestamp,
    metadata: { custom: { liveAssistance: { kind: "ended", liveSessionId: payload.live_session_id } } },
  };
}

function liveErrorToThreadMessage(payload: SupervisorLiveErrorPayload): ThreadMessageLike {
  return {
    id: `live-error-${payload.live_session_id ?? createThreadMessageId("live")}`,
    role: "assistant",
    content: payload.error ?? "Live support hit a problem. Phoebe can keep helping here.",
    createdAt: new Date(),
    metadata: { custom: { liveAssistance: { kind: "error", liveSessionId: payload.live_session_id } } },
  };
}

export function isEnrollmentAdvisorConsultationRequest(value: string | undefined): boolean {
  const text = value?.toLowerCase().replace(/\s+/g, " ").trim() ?? "";
  if (!text) return false;
  if (text.includes("schedule a consultation")) return true;
  const namesEnrollmentAdvisor = text.includes("enrollment advisor") || text.includes("enrollment adviser");
  if (!namesEnrollmentAdvisor) return false;
  return /\b(schedule|book|reserve|set up|setup|appointment|consultation|call|talk|speak|meet)\b/.test(text);
}

function withEnrollmentConsultationMetadata(
  metadata: ChatModelRunResult["metadata"] | undefined,
  triggeringUserText: string | undefined,
): ThreadMessageLike["metadata"] {
  const custom = metadata?.custom ?? {};
  if (!isEnrollmentAdvisorConsultationRequest(triggeringUserText)) {
    return metadata ?? { custom };
  }

  return {
    ...metadata,
    custom: {
      ...custom,
      enrollmentConsultation: {
        kind: "advisor_consultation",
      },
    },
  };
}

export function withAssistantTurnMetadata(
  metadata: ChatModelRunResult["metadata"] | undefined,
  triggeringUserText: string | undefined,
): ThreadMessageLike["metadata"] {
  return withSafetyExitMetadata(
    withEnrollmentConsultationMetadata(metadata, triggeringUserText),
    triggeringUserText,
    { allowLocalFallback: false },
  );
}

export function sanitizeLiveAssistanceMarkdownText(value: string | undefined): string {
  const text = value?.trim();
  if (!text) return "";
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\\/g, "\\\\")
    .replace(/([`*_{}\[\]()#+\-.!|])/g, "\\$1");
}

function createThreadMessageId(prefix: string): string {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
}

function isAsyncGenerator(
  result: Promise<ChatModelRunResult> | AsyncGenerator<ChatModelRunResult, void>,
): result is AsyncGenerator<ChatModelRunResult, void> {
  return typeof (result as AsyncGenerator<ChatModelRunResult, void>)[Symbol.asyncIterator] === "function";
}

function getContentText(content: ThreadMessageLike["content"]): string {
  if (typeof content === "string") return content;
  return content
    .filter((part): part is { type: "text"; text: string } => part.type === "text" && typeof part.text === "string")
    .map((part) => part.text)
    .join("");
}

function textContent(content: ThreadMessageLike["content"]): [{ type: "text"; text: string }] {
  return [{ type: "text", text: getContentText(content) }];
}

// ── Main export ───────────────────────────────────────────────────────────────

export function ChatModal({
  backendUrl,
  silentWidgetAuth = false,
  initialIdentity,
  qualityProfile,
  modelId,
  guestBootstrap,
  analyticsWriteKey,
  disableAnalytics,
  includeMessageBody = false,
  enableDevtools = false,
  layout = "launcher",
  onTraitsResolved,
  genesysLiveChatEnabled,
  genesysLiveChatQueueDisplayEnabled,
  sidePanelShellEnabled,
  strictSidePanelPolicy = false,
  enrollmentConsultationEnabled,
  thinkingInsightsEnabled,
  growthBookClientKey,
  growthBookApiHost,
}: ChatModalProps) {
  useEffect(() => {
    if (silentWidgetAuth && backendUrl) return acquireSilentWidgetAuth(backendUrl);
  }, [silentWidgetAuth, backendUrl]);
  const [resolvedGenesysLiveChatEnabled, setResolvedGenesysLiveChatEnabled] = useState<boolean>(
    genesysLiveChatEnabled ?? false,
  );
  const [resolvedGenesysLiveChatQueueDisplayEnabled, setResolvedGenesysLiveChatQueueDisplayEnabled] = useState<boolean>(
    genesysLiveChatQueueDisplayEnabled ?? false,
  );
  const [resolvedSidePanelShellEnabled, setResolvedSidePanelShellEnabled] = useState<boolean>(
    sidePanelShellEnabled ?? false,
  );
  const [resolvedEnrollmentConsultationEnabled, setResolvedEnrollmentConsultationEnabled] = useState<boolean>(
    enrollmentConsultationEnabled ?? false,
  );
  const [resolvedThinkingInsightsEnabled, setResolvedThinkingInsightsEnabled] = useState<boolean>(
    thinkingInsightsEnabled ?? false,
  );
  const effectiveLayout: ChatModalLayout = strictSidePanelPolicy
    ? resolvedSidePanelShellEnabled ? "side-panel" : "launcher"
    : layout === "side-panel" && resolvedSidePanelShellEnabled ? "side-panel" : "launcher";
  const isSidePanelLayout = effectiveLayout === "side-panel";
  const [chatState, setChatState] = useState<ChatState>(isSidePanelLayout ? "chat" : "idle");
  const [isMinimized, setIsMinimized] = useState(false);
  const hasResolvedInitialSidePanelRef = useRef(isSidePanelLayout || (!strictSidePanelPolicy && layout !== "side-panel"));
  const [isSidePanelMobile, setIsSidePanelMobile] = useState(sidePanelViewportIsMobile);
  const hostDockingControllerRef = useRef<HostDockingController | null>(null);
  const hostDockingLeaseRef = useRef<HostDockingLease | null>(null);
  const isSidePanelMobileRef = useRef(isSidePanelMobile);
  const isMinimizedRef = useRef(isMinimized);
  isSidePanelMobileRef.current = isSidePanelMobile;
  isMinimizedRef.current = isMinimized;

  useEffect(() => {
    if (!isSidePanelLayout || hasResolvedInitialSidePanelRef.current) return;
    hasResolvedInitialSidePanelRef.current = true;
    setChatState("chat");
    setIsMinimized(false);
  }, [isSidePanelLayout]);
  const shellSyncSourceIdRef = useRef(createSyncSourceId());
  const lastAppliedShellSyncAtRef = useRef(0);
  const serverShellSyncPublisherRef = useRef<ServerShellSyncPublisher>(() => undefined);

  const applySyncedShellState = useCallback((syncedState: ChatShellSyncState | null) => {
    if (!syncedState) return;
    if (syncedState.sourceId === shellSyncSourceIdRef.current) return;
    if (syncedState.updatedAt <= lastAppliedShellSyncAtRef.current) return;

    lastAppliedShellSyncAtRef.current = syncedState.updatedAt;
    const nextState = widgetStateFromChatShellSyncState(syncedState);
    setChatState(nextState.chatState);
    setIsMinimized(nextState.isMinimized);
  }, []);

  const publishShellSyncState = useCallback(
    (nextChatState: ChatState, nextIsMinimized: boolean) => {
      const storage = getChatShellSyncStorage();
      const updatedAt = Math.max(Date.now(), lastAppliedShellSyncAtRef.current + 1);
      lastAppliedShellSyncAtRef.current = updatedAt;
      const syncState: ChatShellSyncState = {
        version: 1,
        eventId: createSyncEventId("shell"),
        sourceId: shellSyncSourceIdRef.current,
        status: chatShellStatusForState(nextChatState, nextIsMinimized),
        layout: effectiveLayout,
        updatedAt,
      };
      writeChatShellSyncState(storage, syncState);
      postBroadcastMessage(CHAT_SHELL_SYNC_CHANNEL, syncState);
      serverShellSyncPublisherRef.current(syncState);
    },
    [effectiveLayout],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const storage = getChatShellSyncStorage();
    const storedShellState = parseChatShellSyncState(storage?.getItem(CHAT_SHELL_SYNC_STORAGE_KEY) ?? null);
    if (storedShellState && isFreshChatShellSyncState(storedShellState)) {
      applySyncedShellState(storedShellState);
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== CHAT_SHELL_SYNC_STORAGE_KEY) return;
      applySyncedShellState(parseChatShellSyncState(event.newValue));
    };

    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHAT_SHELL_SYNC_CHANNEL);
    if (channel) {
      channel.onmessage = (event) => {
        applySyncedShellState(parseChatShellSyncState(JSON.stringify(event.data)));
      };
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      channel?.close();
    };
  }, [applySyncedShellState]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handlePageHide = () => {
      publishShellSyncState(chatState, isMinimized);
    };

    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [chatState, isMinimized, publishShellSyncState]);

  useEffect(() => {
    if (!isSidePanelLayout || typeof window === "undefined" || typeof window.matchMedia !== "function") {
      if (!isSidePanelLayout) setIsSidePanelMobile(false);
      return;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${SIDE_PANEL_MOBILE_BREAKPOINT_PX}px)`);
    const syncViewport = () => setIsSidePanelMobile(mediaQuery.matches);
    syncViewport();
    mediaQuery.addEventListener("change", syncViewport);
    window.addEventListener("resize", syncViewport);
    return () => {
      mediaQuery.removeEventListener("change", syncViewport);
      window.removeEventListener("resize", syncViewport);
    };
  }, [isSidePanelLayout]);

  useEffect(() => {
    if (!isSidePanelLayout || typeof document === "undefined") return;

    const controller = getHostDockingController(document);
    if (!controller) return;

    const lease = controller.acquireLease();
    hostDockingControllerRef.current = controller;
    hostDockingLeaseRef.current = lease;

    return () => {
      lease.release();
      hostDockingLeaseRef.current = null;
      hostDockingControllerRef.current = null;

      if (!controller.getSnapshot().geometryActive) {
        clearSidePanelReservationState(document.documentElement);
        dispatchSidePanelState(false, isSidePanelMobileRef.current, isMinimizedRef.current);
      }
    };
  }, [isSidePanelLayout]);

  useEffect(() => {
    if (!isSidePanelLayout || typeof window === "undefined") return;

    const controller = hostDockingControllerRef.current;
    const lease = hostDockingLeaseRef.current;
    if (!controller || !lease) return;

    const currentIsMobile = sidePanelViewportIsMobile();
    if (currentIsMobile !== isSidePanelMobile) setIsSidePanelMobile(currentIsMobile);
    lease.update({
      open: chatState === "chat" && !isMinimized,
      desktop: !currentIsMobile,
    });
    publishSidePanelReservationState(
      document.documentElement,
      controller.getSnapshot().geometryActive,
      currentIsMobile,
      isMinimized,
    );
  }, [chatState, isMinimized, isSidePanelLayout, isSidePanelMobile]);

  useEffect(() => {
    if (growthBookClientKey && !strictSidePanelPolicy) return;
    setResolvedGenesysLiveChatEnabled(genesysLiveChatEnabled ?? false);
    setResolvedGenesysLiveChatQueueDisplayEnabled(genesysLiveChatQueueDisplayEnabled ?? false);
    setResolvedSidePanelShellEnabled(sidePanelShellEnabled ?? false);
    setResolvedEnrollmentConsultationEnabled(enrollmentConsultationEnabled ?? false);
    setResolvedThinkingInsightsEnabled(thinkingInsightsEnabled ?? false);
  }, [
    growthBookClientKey,
    enrollmentConsultationEnabled,
    genesysLiveChatEnabled,
    genesysLiveChatQueueDisplayEnabled,
    sidePanelShellEnabled,
    strictSidePanelPolicy,
    thinkingInsightsEnabled,
  ]);

   // When growthBookClientKey is provided, wire up OpenFeature + the custom
  // GrowthBook provider and re-evaluate the flags once it is ready.
  useEffect(() => {
    if (!growthBookClientKey || strictSidePanelPolicy) return;

    let cancelled = false;
    // Handler refs are captured here so the cleanup closure can call removeHandler
    // with the same function instances that were passed to addHandler.
    let registeredReadyHandler: (() => void) | null = null;
    let registeredConfigurationChangedHandler: (() => void) | null = null;
    let registeredProviderEvents: { Ready: string; ConfigurationChanged: string } | null = null;
    let registeredOpenFeature: { removeHandler: (event: string, handler: () => void) => void } | null = null;

    void (async () => {
      try {
        const [{ OpenFeature, ProviderEvents }, { GrowthBookOpenFeatureProvider }] = await Promise.all([
          import("@openfeature/web-sdk"),
          import("@/runtime/growthbookProvider"),
        ]);

        if (cancelled) return;

        const provider = new GrowthBookOpenFeatureProvider(growthBookClientKey, growthBookApiHost);
        OpenFeature.setProvider(provider);

        const resolveSidePanelFlag = (client: ReturnType<typeof OpenFeature.getClient>) => {
          setResolvedSidePanelShellEnabled(
            client.getBooleanValue(
              "super-phoebe.ui.side-panel.enabled",
              strictSidePanelPolicy ? false : sidePanelShellEnabled ?? false,
            ),
          );
        };

        const resolveFlags = () => {
          if (cancelled) return;
          const client = OpenFeature.getClient();
          setResolvedGenesysLiveChatEnabled(
            client.getBooleanValue("super-phoebe.genesys.live-chat.enabled", genesysLiveChatEnabled ?? false),
          );
          setResolvedGenesysLiveChatQueueDisplayEnabled(
            client.getBooleanValue(
              "super-phoebe.genesys.live-chat.queue-display.enabled",
              genesysLiveChatQueueDisplayEnabled ?? false,
            ),
          );
          if (!strictSidePanelPolicy) resolveSidePanelFlag(client);
          setResolvedEnrollmentConsultationEnabled(
            client.getBooleanValue(
              "super-phoebe.enrollment.consultation.enabled",
              enrollmentConsultationEnabled ?? false,
            ),
          );
          setResolvedThinkingInsightsEnabled(
            client.getBooleanValue("super-phoebe.ui.thinking-insights.enabled", thinkingInsightsEnabled ?? false),
          );
        };

        // Store refs before registering so cleanup can deregister the same instances.
        registeredProviderEvents = ProviderEvents;
        // Cast through unknown: OpenFeature has removeHandler on its public API
        // but the strict type from @openfeature/web-sdk doesn't always surface it
        // depending on the installed version. The runtime object always has it.
        registeredOpenFeature = OpenFeature as unknown as typeof registeredOpenFeature;

        const resolveInitialFlags = () => {
          resolveFlags();
          if (!cancelled && strictSidePanelPolicy) resolveSidePanelFlag(OpenFeature.getClient());
        };
        OpenFeature.addHandler(ProviderEvents.Ready, resolveInitialFlags);
        OpenFeature.addHandler(ProviderEvents.ConfigurationChanged, resolveFlags);
        // The strict artifact policy must wait for provider readiness before it
        // snapshots the side-panel decision. Other flags retain dynamic updates.
        if (!strictSidePanelPolicy) resolveFlags();
        registeredReadyHandler = resolveInitialFlags;
        registeredConfigurationChangedHandler = resolveFlags;
      } catch (err) {
        // Keep prop/default values if provider setup fails. Unavailable Phase 1
        // capability entry points stay fail-closed.
        if (typeof console !== "undefined") {
          console.warn("[phoebe-chat] OpenFeature/GrowthBook provider setup failed:", err);
        }
      }
    })();

    return () => {
      cancelled = true;
      // Deregister handlers from the OpenFeature global singleton to prevent
      // accumulation across remounts (minimize/reopen, React StrictMode double-invoke).
      if (registeredOpenFeature && registeredProviderEvents && registeredReadyHandler && registeredConfigurationChangedHandler) {
        registeredOpenFeature.removeHandler(registeredProviderEvents.Ready, registeredReadyHandler);
        registeredOpenFeature.removeHandler(registeredProviderEvents.ConfigurationChanged, registeredConfigurationChangedHandler);
      }
    };
  }, [
    growthBookClientKey,
    growthBookApiHost,
    enrollmentConsultationEnabled,
    genesysLiveChatEnabled,
    genesysLiveChatQueueDisplayEnabled,
    sidePanelShellEnabled,
    strictSidePanelPolicy,
    thinkingInsightsEnabled,
  ]);

  // Initialise analytics for library-mode consumers (write key passed as prop).
  // IIFE-mode consumers initialise via iife.ts before mount.
  useEffect(() => {
    if (disableAnalytics) {
      disable();
    } else if (analyticsWriteKey) {
      void init(analyticsWriteKey);
    }
  // Run once on mount only — write key and disable flag are not expected to change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleButtonClick = () => {
    if (chatState !== "idle") return;
    trackUOPX(UOPX_EVENT.CHAT_WITH_AGENT, UOPX_TEXT.PHOEBE_AI_ASSISTANT, UOPX_LOCATION.ICON);
    setChatState("chat");
    setIsMinimized(false);
    publishShellSyncState("chat", false);
    if (backendUrl) {
      sendWidgetTelemetry({ backendUrl, eventName: "chat_opened" });
    }
  };

  const handleClose = () => {
    trackUOPX(UOPX_EVENT.CLOSE, UOPX_TEXT.CLOSE, UOPX_LOCATION.HEADER);
    setIsMinimized(false);
    setChatState("idle");
    publishShellSyncState("idle", false);
  };

  const handleMinimize = () => {
    if (chatState !== "chat") return;
    trackUOPX(UOPX_EVENT.CLOSE, UOPX_TEXT.CLOSE, UOPX_LOCATION.HEADER);
    setIsMinimized(true);
    publishShellSyncState("chat", true);
  };

  const handleReopen = () => {
    setIsMinimized(false);
    publishShellSyncState("chat", false);
  };

  const contentProps: ModalContentProps = {
    chatState,
    isMinimized,
    unreadCount: 0,
    enableDevtools,
    layout: effectiveLayout,
    shellSyncSourceId: shellSyncSourceIdRef.current,
    serverShellSyncPublisherRef,
    onServerShellSyncState: applySyncedShellState,
    onButtonClick: handleButtonClick,
    onClose: handleClose,
    onMinimize: handleMinimize,
    onReopen: handleReopen,
  };

  const runtimeConfigValue = useMemo(
    () => ({
      genesysLiveChatEnabled: resolvedGenesysLiveChatEnabled,
      genesysLiveChatQueueDisplayEnabled: resolvedGenesysLiveChatQueueDisplayEnabled,
      sidePanelShellEnabled: resolvedSidePanelShellEnabled,
      enrollmentConsultationEnabled: resolvedEnrollmentConsultationEnabled,
      thinkingInsightsEnabled: resolvedThinkingInsightsEnabled,
    }),
    [
      resolvedEnrollmentConsultationEnabled,
      resolvedGenesysLiveChatEnabled,
      resolvedGenesysLiveChatQueueDisplayEnabled,
      resolvedSidePanelShellEnabled,
      resolvedThinkingInsightsEnabled,
    ],
  );

  return (
    <RuntimeConfigContext.Provider value={runtimeConfigValue}>
      <SocketIORuntime
        backendUrl={backendUrl}
        initialIdentity={initialIdentity}
        qualityProfile={qualityProfile}
        modelId={modelId}
        guestBootstrap={guestBootstrap}
        includeMessageBody={includeMessageBody}
        onTraitsResolved={onTraitsResolved}
        {...contentProps}
      />
    </RuntimeConfigContext.Provider>
  );
}
